import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./about-tv.css";
import { getLights, subscribeLights, subscribeBeat } from "../lib/lightsBus";
import { setTvScreenHost } from "../lib/tvScreen";
import { plywoodDataUrl } from "./plywood";

// ===========================================================================================
// The TV on the About page.
// A small steel television on a beam of light. The words of the About page are its screen. Tap the screen and the page decides what
// happens (onScreenTap): on the About page it plays the member's Favorites inside the screen (the player puts its picture there,
// see lib/tvScreen.js). The "Read the text" button under the TV grows the TV until the words fill the window. It turns slowly (and
// stands straight while a pointer or finger is on it, or while a song plays on its screen unless "Spin while it plays" is on), the
// vents show an atom swirling inside, a force field holds it together (three looks, picked with the switch under the TV), and the
// glow around it follows the song that is playing (see lib/lightsBus.js). Under the TV: seven finishes (Steel, Wood, Mint, Cherry,
// Space age, Gold, Quartz), "Turn it on again", a spin switch, Classic mode (the TV is a wider one with a 16:9 screen, Cinema mode, unless this is switched on), a light bulb (the page opens the player's Lights panel) and a lights row (follow the
// song, or Frost, Flames, Fire and Ice, Haunted Night, Maypole). Finish, force field, lights and Classic mode are remembered on this device. The page that uses it passes the words in as children; the original wording is also
// written into frontend/public/index.html for programs that do not run JavaScript.
// Every class starts with atv- (about-tv.css) so nothing clashes with the rest of the site.
// ===========================================================================================

// The body of the TV is a stack of thin slices, each one a layer the graphics chip has to hold. The engine (layout) switches on only as many as
// the screen needs and a phone's graphics memory can hold (a phone that runs short draws black blocks).
// The TV is sized as if the browser were at 100% zoom, and that size is locked: on a computer, zooming in or out (Ctrl and scroll) does not make the TV
// bigger or smaller (its picture is made 1/zoom as many CSS pixels, so it stays the same on the screen), and a zoomed window never switches to the phone
// layout. The page cannot ask the browser for its zoom, so it is worked out from the window's outer width (which a zoom does not change) over its inner
// width (which it does), and only believed when it is a normal zoom step on a normal display scale. Phones and tablets (a touch screen) are never changed.
const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5];
const DISPLAY_SCALES = [1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 3, 4];
// A touch device: a phone, a tablet, a TV with a remote. An iPad asking for the "desktop website" says it has a mouse, so it is also found by its touch points.
const touchDevice = () => {
  try {
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return true;
    return (navigator.maxTouchPoints || 0) > 1 && /Mac|iPad|iPhone/i.test(navigator.platform || navigator.userAgent || "");
  } catch { return false; }
};
const pageZoom = () => {
  try {
    if (touchDevice()) return 1;
    const iw = window.innerWidth, ow = window.outerWidth;
    if (!iw || !ow) return 1;
    let best = 1, bd = 9;
    for (const z of ZOOM_STEPS) { const d = Math.abs(Math.log(ow / iw / z)); if (d < bd) { bd = d; best = z; } }
    if (bd > 0.07) return 1;
    const base = (window.devicePixelRatio || 1) / best;
    return DISPLAY_SCALES.some((b) => Math.abs(base - b) < 0.06) ? best : 1;
  } catch { return 1; }
};
// a narrow or short window (as it would be at 100% zoom) gets the phone layout: one row of buttons and an Options panel (about-tv.css, data-compact)
const compactNow = () => { try { const z = pageZoom(); return (document.documentElement.clientWidth || window.innerWidth) * z <= 699 || window.innerHeight * z <= 520; } catch { return false; } };
const MAX_SLICES = 112;                   // the body is a stack of thin slices; the engine switches on as many as the screen can use (see layout)
const SLICE_KEYS = Array.from({ length: MAX_SLICES }, (_, i) => i);
const sliceAlpha = (k, n) => (0.05 + 0.5 * ((k + 1) / (n + 1))).toFixed(3);
const WALL_SEGS = 6;                       // the two flat side walls inside the body (see layout): 6 planes a side follow its curve
const WALL_KEYS = Array.from({ length: 2 * WALL_SEGS }, (_, i) => i);

const Vent = ({ side }) => (
  <div className="atv-vent"><div className="atv-atoms" data-side={side}><canvas aria-hidden="true" /></div></div>
);

const FINISHES = [["steel", "Steel"], ["wood", "Wood"], ["mint", "Mint"], ["cherry", "Cherry"], ["space", "Space age"], ["gold", "Gold"], ["quartz", "Quartz"]];
const FORCE_LOOKS = [["diamond", "Diamond"], ["octagon", "Octagon"], ["grid", "Grid"]];
const LIGHT_PICKS = [["song", "Follow the song"], ["frost", "Frost"], ["flames", "Flames"], ["fireice", "Fire and Ice"], ["haunted", "Haunted Night"], ["maypole", "Maypole"]];
// Cinema mode is how the TV is shown: drawn wider and shorter so its screen is a true 16:9 (a widescreen song fills it, with no black bars). The "Classic mode" switch
// in Options goes back to the older, taller shape. The choice is kept on this device (only "classic" is written down, so a new visitor gets Cinema mode).
const DESIGN_W = 430, DESIGN_W_CINEMA = 620;        // the width the whole TV picture is drawn at (see fit): the classic one, and Cinema mode's wider one
const CLASSIC_KEY = "dtm_tv_classic";
const cinemaSaved = () => { try { return localStorage.getItem(CLASSIC_KEY) !== "1"; } catch { return true; } };

const BulbIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.9.7 1.6 1.7 1.6 2.9V17h4v-.3c0-1.2.7-2.2 1.6-2.9A6 6 0 0 0 12 3z" />
  </svg>
);

export default function AboutTv({ children, onScreenTap, onScreen = false, note = "", onEdit, onLights, ready = true }) {
  const rootRef = useRef(null);
  const vidRef = useRef(null);
  const [optsOpen, setOptsOpen] = useState(false);              // the Options panel (only used on a phone-sized window)
  const tapRef = useRef(onScreenTap);
  tapRef.current = onScreenTap;
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    el.setAttribute("data-compact", compactNow() ? "1" : "0");
    el.setAttribute("data-cinema", cinemaSaved() ? "1" : "0");     // before the first paint, so the TV never shows in the wrong shape for a moment
  }, []);
  useEffect(() => startTv(rootRef.current, () => tapRef.current), []);
  // The player puts its <video> into the screen while a song plays here. This is a layout effect so the picture is handed back to
  // the player before the screen leaves the page (a video taken out of the page would stop).
  useLayoutEffect(() => {
    setTvScreenHost(vidRef.current);
    return () => setTvScreenHost(null);
  }, []);
  useEffect(() => {
    if (!optsOpen) return undefined;
    const esc = (e) => { if (e.key === "Escape") setOptsOpen(false); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [optsOpen]);
  // the TV stands straight, facing the person, while a song plays on its screen
  useEffect(() => {
    const t = rootRef.current;
    if (t && t.__atv) t.__atv.hold("video", onScreen);
  }, [onScreen]);
  return (
    <div className="atv-stage" ref={rootRef} data-testid="about-tv">
      <div className="atv-cone" data-halo="pulse">
        <canvas className="atv-beam" aria-hidden="true" />
        <div className="atv-slot">
          <div className="atv-aura" aria-hidden="true"><canvas /><div className="atv-halo" /></div>
          <canvas className="atv-field" aria-hidden="true" />
          <div className="atv-turn">
            {SLICE_KEYS.map((i) => <i key={i} className="atv-slice" aria-hidden="true" />)}
            {WALL_KEYS.map((i) => <i key={i} className="atv-wall" aria-hidden="true" />)}
            <div className="atv-back" aria-hidden="true">
              <Vent side="back" />
              <div className="atv-io">
                <div className="atv-hdmi"><div className="atv-port"><i /><b>HDMI 1</b></div><div className="atv-port"><i /><b>HDMI 2</b></div><div className="atv-port"><i /><b>HDMI 3</b></div></div>
                <div className="atv-rca"><div className="atv-jack"><i /><b>VIDEO</b></div><div className="atv-jack atv-jw"><i /><b>AUDIO L</b></div><div className="atv-jack atv-jr"><i /><b>AUDIO R</b></div></div>
              </div>
              <span className="atv-badge">DAN THE MAN TV</span>
            </div>
            <div className="atv-front">
              <div className="atv-pod">
                <div className="atv-plate" aria-hidden="true" />
                <div className="atv-bezel">
                  <div className="atv-screen" data-vid={onScreen ? "1" : undefined} data-ready={ready ? "1" : "0"}>
                    <div className="atv-content">{children}</div>
                    <div className="atv-vid" ref={vidRef} data-testid="about-tv-video-host" />
                    <div className="atv-fade" aria-hidden="true" />
                    <div className="atv-tap" aria-hidden="true"><span className="atv-badge">PLAY MY FAVORITES</span></div>
                    <div className="atv-note" role="status" aria-live="polite" hidden={!note} data-testid="about-tv-note">{note}</div>
                    <div className="atv-scan" aria-hidden="true" />
                    <div className="atv-glare" aria-hidden="true" />
                    <button type="button" className="atv-open" aria-label={onScreen ? "Pause or play the song on the TV screen" : "Play my favorite songs on the TV screen"} data-testid="about-tv-open" />
                    <button type="button" className="atv-x" aria-label="Close" data-testid="about-tv-close" hidden>&#x2715;</button>
                  </div>
                </div>
                <div className="atv-chin">
                  <div className="atv-knob atv-k1" aria-hidden="true"><div className="atv-face" /><div className="atv-tick" /></div>
                  <div className="atv-knob atv-k2" aria-hidden="true"><div className="atv-face" /><div className="atv-tick" /></div>
                  <div className="atv-grille-wrap" aria-hidden="true"><div className="atv-grille" /></div>
                  <button type="button" className="atv-btn-bt" aria-haspopup="dialog" aria-expanded="false" aria-controls="atv-btpop" aria-label="Bluetooth: how to play through a speaker or headphones" title="Bluetooth" data-testid="about-tv-bluetooth">
                    <span className="atv-face" />
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path className="atv-bo" d="M6.6 7.6l10.8 9L12 21V3l5.4 4.4-10.8 9" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      <path className="atv-bi" d="M6.6 7.6l10.8 9L12 21V3l5.4 4.4-10.8 9" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <div className="atv-led" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
          <div className="atv-turn2" aria-hidden="true">
            <div className="atv-sv"><Vent side="left" /></div>
            <div className="atv-sv"><Vent side="right" /></div>
          </div>
        </div>
        <div className="atv-stand" aria-hidden="true">
          <div className="atv-shaft" />
          <div className="atv-foot" />
        </div>
      </div>
      <div className="atv-portal" />
      <div className="atv-btpop" id="atv-btpop" role="dialog" aria-labelledby="atv-btpop-t" hidden>
        <div className="atv-glass">
          <p className="atv-t" id="atv-btpop-t">Play it through Bluetooth</p>
          <p>Pair a Bluetooth speaker or headphones in your phone&rsquo;s or computer&rsquo;s Bluetooth settings, then come back and play a song. The sound comes out of your speaker.</p>
          <button type="button" className="atv-badge atv-ok">Got it</button>
        </div>
      </div>
      <div className="atv-bar" data-opts={optsOpen ? "open" : "closed"}>
        <div className="atv-acts">
          <button type="button" className="atv-badge atv-pill atv-read" aria-expanded="false" data-testid="about-tv-read">Read the text</button>
          {onLights && <button type="button" className="atv-badge atv-pill atv-bulb" onClick={onLights} aria-label="Change the lights for the song that is playing" data-testid="about-tv-bulb"><BulbIcon />Lights</button>}
          <button type="button" className="atv-badge atv-pill atv-opts-btn" onClick={() => setOptsOpen((v) => !v)} aria-expanded={optsOpen} aria-controls="atv-opts" data-testid="about-tv-options">Options</button>
        </div>
        <div className="atv-opts" id="atv-opts" role="group" aria-label="TV options">
          <button type="button" className="atv-opts-x" onClick={() => setOptsOpen(false)} aria-label="Close the options" data-testid="about-tv-options-close">&#x2715;</button>
          <div className="atv-acts atv-acts2">
            <button type="button" className="atv-badge atv-pill" data-replay="1" data-testid="about-tv-replay">Turn it on again</button>
            <button type="button" className="atv-badge atv-pill" data-spin="1" aria-pressed="false" title="Keep the TV turning while a song plays on its screen" data-testid="about-tv-spin">Spin while it plays</button>
            <button type="button" className="atv-badge atv-pill" data-classic-btn="1" aria-pressed="false" title="Switch to the older, taller TV shape (the default is the wide Cinema shape)" data-testid="about-tv-classic">Classic mode</button>
            {onEdit && <button type="button" className="atv-badge atv-pill atv-edit" onClick={() => { setOptsOpen(false); onEdit(); }} data-testid="about-edit">Edit text</button>}
          </div>
          <div className="atv-looks" role="group" aria-label="TV finish">
            <label className="atv-looks-t" htmlFor="atv-sel-fin">Finish</label>
            <select id="atv-sel-fin" className="atv-badge atv-sel" data-fin-sel="1" data-testid="about-tv-finish">
              {FINISHES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>
          <div className="atv-looks" role="group" aria-label="Force field look">
            <label className="atv-looks-t" htmlFor="atv-sel-ff">Force field</label>
            <select id="atv-sel-ff" className="atv-badge atv-sel" data-ff-sel="1" data-testid="about-tv-look">
              {FORCE_LOOKS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>
          <div className="atv-looks" role="group" aria-label="Lights on the TV">
            <label className="atv-looks-t" htmlFor="atv-sel-lit">TV lights</label>
            <select id="atv-sel-lit" className="atv-badge atv-sel" data-lit-sel="1" data-testid="about-tv-lights">
              {LIGHT_PICKS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================================
// The engine. Everything below runs in the browser against the markup above, and undoes itself when the page is left.
// ===========================================================================================

const TWO = Math.PI * 2;
const PERSP_PER_W = 6.6;                  // the CSS perspective of .atv-slot is this many times the TV's width, so the set looks the same at any size or zoom (2400 px at the usual 364 px wide)
const SPEED = 14;                         // degrees a second, about 26 seconds a round

function hsl2rgb(h, sat, lig) {
  const s = sat / 100, l = lig / 100, a = s * Math.min(l, 1 - l);
  const kf = (n) => (n + h / 30) % 12;
  const f = (n) => l - a * Math.max(-1, Math.min(kf(n) - 3, Math.min(9 - kf(n), 1)));
  return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
}
const lift = (c, t) => c.map((v) => Math.round(v + (255 - v) * t));
const rnd = (a, b) => a + Math.random() * (b - a);

// What the TV shows when no song is playing: the blue and the pink. A song's lights replace it (see view()).
const DEFAULT_VIEW = {
  rgb: [hsl2rgb(215, 100, 58), hsl2rgb(325, 100, 58), hsl2rgb(325, 100, 58)],
  atom: [hsl2rgb(215, 100, 62), hsl2rgb(325, 100, 66)],
  style: "pulse", per: 5.5, spin: 7, glow: 42, fx: null, beat: false, reach: 6,
};
// A note from lightsBus (or null) -> what the TV draws.
function view(raw) {
  if (!raw) return DEFAULT_VIEW;
  if (raw.off) return { ...DEFAULT_VIEW, style: "none", glow: 0, fx: null };
  const rgb = Array.isArray(raw.rgb) && raw.rgb.length >= 2 ? raw.rgb : DEFAULT_VIEW.rgb;
  const dark = (c) => c[0] * 0.3 + c[1] * 0.59 + c[2] * 0.11 < 40;      // black would vanish on the dark glass
  const a = dark(rgb[0]) ? [96, 98, 104] : rgb[0], b = dark(rgb[1]) ? [96, 98, 104] : rgb[1];
  return {
    rgb: [rgb[0], rgb[1], rgb[2] || rgb[1]],
    atom: [lift(a, 0.1556), lift(b, 0.2444)],
    style: raw.style || "pulse", per: raw.per || 2.8, spin: raw.spin || 6, glow: raw.glow || 60, fx: raw.fx || null,
    beat: !!raw.beat, reach: raw.reach || 6,
  };
}

// The effects that come out from behind the TV. Each is a mix of particle kinds, with how many a second.
const FXDEF = {
  frost: { parts: [["snow", 20]] },
  flames: { parts: [["fire", 55]] },
  fireice: { parts: [["snow", 20], ["fire", 20]] },
  blueflame: { parts: [["blue", 55]] },
  ember: { parts: [["ember", 14]] },
  haunted: { parts: [["mist", 7]], bats: 1.3 },
  batflight: { parts: [], bats: 0.7 },
  ghosts: { parts: [["ghost", 6]] },
};

function startTv(root, getTap) {
  if (!root) return undefined;
  const q = (sel) => root.querySelector(sel);
  const cone = q(".atv-cone"), slot = q(".atv-slot"), turn = q(".atv-turn"), front = q(".atv-front"), portal = q(".atv-portal"),
    pod = q(".atv-pod"), screen = q(".atv-screen"), content = q(".atv-content"), openBtn = q(".atv-open"), readBtn = q(".atv-read"), closeBtn = q(".atv-x"),
    back = q(".atv-back"), turn2 = q(".atv-turn2"), standEl = q(".atv-stand"), footEl = q(".atv-foot"), beamCv = q(".atv-beam"),
    fxc = q(".atv-aura canvas"), btn = q(".atv-btn-bt"), btpop = q(".atv-btpop"), btOk = q(".atv-ok");
  const svs = [...turn2.querySelectorAll(".atv-sv")];
  const allSlices = [...turn.querySelectorAll(".atv-slice")];
  const walls = [...turn.querySelectorAll(".atv-wall")];
  let slices = allSlices, N = allSlices.length, shown = 0, PERSP = 2400;
  // the solid body (TvBodyGl.js, Three.js): once it is up, the slices and walls are switched off
  let glBody = null, glOn = false;
  const g2 = fxc.getContext("2d"), beamCtx = beamCv.getContext("2d");
  const mq = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  let reduce = !!(mq && mq.matches);

  let alive = true, isOpen = false, busy = false, ghost = null;
  const rafs = new Set(), timers = new Set(), offs = [];
  const on = (target, type, fn, opts) => { target.addEventListener(type, fn, opts); offs.push(() => target.removeEventListener(type, fn, opts)); };
  const raf = (fn) => { const id = requestAnimationFrame((t) => { rafs.delete(id); if (alive) fn(t); }); rafs.add(id); return id; };
  const later = (fn, ms) => { const id = setTimeout(() => { timers.delete(id); if (alive) fn(); }, ms); timers.add(id); return id; };

  document.body.classList.add("atv-clip");

  // ---------- the body: thin slices that narrow toward the back, like a picture tube ----------
  let D = 0, D2 = 0, W0 = 0, LIMIT = 9999, BELLY = 0, curSc = 1, scFix = 1;
  // The set is drawn once, at its design size (430 px wide), and the whole drawing is scaled by SC to fit the window (see fit). Everything below works in the
  // design size; a size read from the page (getBoundingClientRect) is on-screen size, so it is divided by SC first.
  let SC = 1;
  const wid = (t) => 1 - 0.2 * (1 - Math.cos(Math.PI * t / 2));
  const belly = (t) => BELLY * Math.pow(Math.sin(Math.PI * t), 1.4);

  // ---------- the lights: the glow around the set, the effect behind it, the atom's colors ----------
  let cur = DEFAULT_VIEW;
  const atomRGB = { a: DEFAULT_VIEW.atom[0], b: DEFAULT_VIEW.atom[1] };
  const P = [], sprites = {};
  let fxDef = null, fxRAF = 0, fxLast = 0, fxW = 0, fxH = 0, tvR = { x: 0, y: 0, w: 0, h: 0 }, fxAcc = [], batAcc = 0;

  function fxSize() {
    const so = slot.getBoundingClientRect(), vw = document.documentElement.clientWidth, sw = so.width / SC, sh = so.height / SC, up = 0.3 * sh, down = 0.2 * sh;
    const side = Math.max(0, Math.min(0.3 * sw, so.left / SC, (vw - so.right) / SC));
    fxc.style.left = `${-side}px`; fxc.style.right = `${-side}px`; fxc.style.width = `${sw + 2 * side}px`; fxc.style.top = `${-up}px`; fxc.style.height = `${sh + up + down}px`;
    const r = fxc.getBoundingClientRect(), dpr = Math.min(2, window.devicePixelRatio || 1) * Math.max(SC, 0.75);
    fxW = r.width / SC; fxH = r.height / SC; fxc.width = Math.max(1, Math.round(fxW * dpr)); fxc.height = Math.max(1, Math.round(fxH * dpr)); g2.setTransform(dpr, 0, 0, dpr, 0, 0);
    const sr = slot.getBoundingClientRect(); tvR = { x: (sr.left - r.left) / SC, y: (sr.top - r.top) / SC, w: sr.width / SC, h: sr.height / SC };
  }
  function sprite(rgb) {
    const key = rgb.join(",");
    if (sprites[key]) return sprites[key];
    const c = document.createElement("canvas"); c.width = 48; c.height = 48;
    const x = c.getContext("2d"), gr = x.createRadialGradient(24, 24, 0, 24, 24, 24);
    gr.addColorStop(0, `rgba(${key},1)`); gr.addColorStop(0.35, `rgba(${key},.55)`); gr.addColorStop(1, `rgba(${key},0)`);
    x.fillStyle = gr; x.fillRect(0, 0, 48, 48);
    return (sprites[key] = c);
  }
  function spawn(kind) {
    const th = Math.random() * TWO, cx = tvR.x + tvR.w / 2, cy = tvR.y + tvR.h / 2;
    let nx = Math.cos(th), ny = Math.sin(th), p;
    if (kind === "snow") {
      p = { t: "snow", x: cx + nx * tvR.w * 0.47, y: cy + ny * tvR.h * 0.47, vx: nx * rnd(35, 95), vy: ny * rnd(35, 95) - 10, g: 30, life: rnd(2.2, 3.4), size: rnd(3, 7), rgb: [200, 232, 255], star: Math.random() < 0.4 };
    } else if (kind === "fire" || kind === "blue") {
      nx = Math.random() < 0.5 ? -1 : 1; ny = rnd(0.1, 1);   // flames start low on the left and right edges and climb
      const blue = kind === "blue";
      p = { t: "fire", x: cx + nx * (tvR.w * 0.5 + rnd(-4, 10)), y: cy + ny * tvR.h * 0.5, vx: nx * rnd(2, 14), vy: -rnd(55, 130), g: 0, life: rnd(0.8, 1.5), size: rnd(11, 24),
        rgb: blue ? (Math.random() < 0.5 ? [70, 150, 255] : [40, 100, 255]) : (Math.random() < 0.5 ? [255, 150, 30] : [255, 90, 20]), hot: blue ? [170, 225, 255] : [255, 190, 60] };
    } else if (kind === "ember") {
      p = { t: "ember", x: cx + rnd(-0.45, 0.45) * tvR.w, y: cy + tvR.h * rnd(0.35, 0.5), vx: rnd(-10, 10), vy: -rnd(22, 55), g: 0, life: rnd(2.6, 4.2), size: rnd(3, 6.5), rgb: [255, 175, 70], ph: rnd(0, 6) };
    } else if (kind === "mist") {
      p = { t: "mist", x: cx + nx * tvR.w * 0.47, y: cy + ny * tvR.h * 0.47, vx: nx * rnd(14, 34), vy: ny * rnd(14, 34) - rnd(6, 20), g: 0, life: rnd(2.6, 4.2), size: rnd(26, 52), rgb: Math.random() < 0.6 ? [150, 80, 230] : [255, 140, 40], a: 0.22 };
    } else {   // ghost
      p = { t: "mist", x: cx + nx * tvR.w * 0.47, y: cy + ny * tvR.h * 0.47, vx: nx * rnd(8, 20), vy: ny * rnd(8, 20) - rnd(10, 26), g: 0, life: rnd(3, 5), size: rnd(30, 56), rgb: Math.random() < 0.6 ? [190, 240, 255] : [200, 170, 255], a: 0.2 };
    }
    p.age = 0; P.push(p);
  }
  function bat(side) {
    const cx = tvR.x + tvR.w / 2, cy = tvR.y + tvR.h * 0.35, dir = side ? 1 : -1;
    P.push({ t: "bat", x: cx + dir * tvR.w * 0.3, y: cy, vx: dir * rnd(40, 70), vy: -rnd(25, 55), g: 0, age: 0, life: rnd(3, 4.4), size: rnd(13, 21), ph: rnd(0, 6), rgb: [255, 150, 40] });
  }
  function batPath(g, flap) {
    const u = -0.6 * flap;
    g.beginPath(); g.moveTo(0, -0.22); g.lineTo(0.09, -0.34); g.lineTo(0.15, -0.2);
    g.quadraticCurveTo(0.55, -0.45 + u * 0.6, 1.05, -0.1 + u); g.quadraticCurveTo(0.9, 0.08 + u * 0.6, 0.72, 0.1 + u * 0.3); g.quadraticCurveTo(0.6, 0.2 + u * 0.3, 0.42, 0.14);
    g.quadraticCurveTo(0.28, 0.26, 0, 0.42); g.quadraticCurveTo(-0.28, 0.26, -0.42, 0.14); g.quadraticCurveTo(-0.6, 0.2 + u * 0.3, -0.72, 0.1 + u * 0.3);
    g.quadraticCurveTo(-0.9, 0.08 + u * 0.6, -1.05, -0.1 + u); g.quadraticCurveTo(-0.55, -0.45 + u * 0.6, -0.15, -0.2); g.lineTo(-0.09, -0.34); g.closePath();
  }
  function fxFrame(t) {
    fxRAF = 0;
    const dt = Math.min(0.05, (t - fxLast) / 1000); fxLast = t;
    if (!document.hidden) {
      if (fxDef) {
        fxDef.parts.forEach((part, i) => { fxAcc[i] = (fxAcc[i] || 0) + part[1] * dt; while (fxAcc[i] >= 1) { fxAcc[i] -= 1; spawn(part[0]); } });
        if (fxDef.bats) { batAcc += dt; if (batAcc > fxDef.bats) { batAcc = 0; bat(Math.random() < 0.5); } }
      }
      g2.clearRect(0, 0, fxW, fxH);
      for (let i = P.length - 1; i >= 0; i--) {
        const p = P[i]; p.age += dt;
        if (p.age > p.life) { P.splice(i, 1); continue; }
        p.vy += p.g * dt; p.x += p.vx * dt; p.y += p.vy * dt;
        const f = p.age / p.life, a = f < 0.15 ? f / 0.15 : 1 - (f - 0.15) / 0.85;
        if (p.t === "snow") {
          g2.globalCompositeOperation = "source-over"; g2.globalAlpha = a * 0.9; g2.drawImage(sprite(p.rgb), p.x - p.size * 1.6, p.y - p.size * 1.6, p.size * 3.2, p.size * 3.2);
          if (p.star) { g2.strokeStyle = `rgba(235,248,255,${(a * 0.9).toFixed(2)})`; g2.lineWidth = 1; g2.beginPath(); g2.moveTo(p.x - p.size, p.y); g2.lineTo(p.x + p.size, p.y); g2.moveTo(p.x, p.y - p.size); g2.lineTo(p.x, p.y + p.size); g2.stroke(); }
        } else if (p.t === "fire") {
          const sz = p.size * (1 - f * 0.7); g2.globalCompositeOperation = "lighter"; g2.globalAlpha = a * 0.85; g2.drawImage(sprite(f < 0.45 ? p.hot : p.rgb), p.x - sz, p.y - sz, sz * 2, sz * 2);
        } else if (p.t === "ember") {
          const sz = p.size * (1 - f * 0.4) * 2.4; g2.globalCompositeOperation = "lighter"; g2.globalAlpha = a * 0.9 * (0.75 + 0.25 * Math.sin(p.age * 9 + p.ph)); g2.drawImage(sprite(p.rgb), p.x - sz, p.y - sz, sz * 2, sz * 2);
        } else if (p.t === "mist") {
          g2.globalCompositeOperation = "lighter"; g2.globalAlpha = a * p.a; g2.drawImage(sprite(p.rgb), p.x - p.size, p.y - p.size, p.size * 2, p.size * 2);
        } else if (p.t === "bat") {
          g2.globalCompositeOperation = "source-over"; g2.globalAlpha = Math.min(1, a * 1.6) * 0.95; g2.save(); g2.translate(p.x, p.y + Math.sin(p.age * 2.2 + p.ph) * 6); g2.scale(p.size, p.size);
          batPath(g2, Math.sin(p.age * 11 + p.ph)); g2.fillStyle = `rgb(${p.rgb.join(",")})`; g2.shadowColor = "rgba(255,150,40,.9)"; g2.shadowBlur = 10; g2.fill(); g2.restore();
        }
      }
      g2.globalAlpha = 1; g2.globalCompositeOperation = "source-over";
      drawRings(t);
    }
    if (fxDef || P.length || rings.length) fxRAF = raf(fxFrame); else g2.clearRect(0, 0, fxW, fxH);
  }
  // ---------- the boom: while a song on Beat lights plays in the TV's screen, the glow flashes on every real beat and a blast of light rushes out ----------
  // The player listens to the sound and sends each beat (lib/lightsBus.js). The glow brightens and swells a little; a ring of the song's colors
  // leaves the frame and fades, going farther for a harder beat and a longer "reach". If no beats arrive (the sound cannot be read) the glow keeps its steady pulse.
  const rings = [];
  let boomEnv = 0, boomPeak = 1, boomHold = 0, boomAt = -1e9, boomRAF = 0, boomLast = 0, ringTurn = 0;
  function endBoom() {
    rings.length = 0; boomEnv = 0; boomHold = 0; boomAt = -1e9;
    cone.removeAttribute("data-boom"); cone.style.removeProperty("--atv-boom");
  }
  function boomFrame(t) {
    boomRAF = 0;
    const dt = boomLast ? Math.min(100, t - boomLast) : 16; boomLast = t;
    if (boomHold > 0) { boomEnv += (boomPeak - boomEnv) * Math.min(1, dt / 45); boomHold -= dt; } else boomEnv *= Math.exp(-dt / 220);
    cone.style.setProperty("--atv-boom", boomEnv.toFixed(3));
    if (t - boomAt > 3500 && boomEnv < 0.01 && !rings.length) { endBoom(); boomLast = 0; return; }
    boomRAF = raf(boomFrame);
  }
  function onBeat(strength) {
    if (reduce || !cur.beat || cur.style === "none") return;
    const now = performance.now(), s = Math.max(0.25, Math.min(1, +strength || 0.6));
    boomAt = now; boomHold = 70; boomPeak = 0.35 + 0.65 * s;
    cone.setAttribute("data-boom", "1");
    const f = (0.3 + 0.7 * ((cur.reach || 6) / 10)) * (0.3 + 0.7 * s);        // 0.09 .. 1, how far out the blast goes
    rings.push({ t0: now, dur: 480 + 420 * f, f, a: Math.min(1, 0.62 + 0.38 * s) * (0.6 + 0.4 * (cur.glow / 100)), rgb: cur.rgb[ringTurn++ % 2] });
    if (rings.length > 3) rings.shift();
    if (!boomRAF) { boomLast = 0; boomRAF = raf(boomFrame); }
    if (!fxRAF) { fxLast = performance.now(); fxRAF = raf(fxFrame); }
  }
  function ringPath(x, y, w, h, r) {
    const k = Math.max(0, Math.min(r, w / 2, h / 2));
    g2.beginPath(); g2.moveTo(x + k, y); g2.lineTo(x + w - k, y); g2.arcTo(x + w, y, x + w, y + k, k); g2.lineTo(x + w, y + h - k); g2.arcTo(x + w, y + h, x + w - k, y + h, k);
    g2.lineTo(x + k, y + h); g2.arcTo(x, y + h, x, y + h - k, k); g2.lineTo(x, y + k); g2.arcTo(x, y, x + k, y, k); g2.closePath();
  }
  function drawRings(t) {
    if (!rings.length) return;
    const side = Math.max(0, Math.min(tvR.x, fxW - tvR.x - tvR.w)), up = Math.max(0, tvR.y), down = Math.max(0, fxH - tvR.y - tvR.h);
    for (let i = rings.length - 1; i >= 0; i--) {
      const r = rings[i], u = (performance.now() - r.t0) / r.dur;
      if (u >= 1) { rings.splice(i, 1); continue; }
      const e = 1 - Math.pow(1 - u, 2.6), al = r.a * Math.min(1, u / 0.06) * Math.pow(1 - u, 1.2);
      const dx = side * r.f * e, du = up * r.f * e, dd = down * r.f * e, rgb = r.rgb.join(",");
      g2.globalCompositeOperation = "lighter"; g2.shadowColor = `rgba(${rgb},${(al * 0.9).toFixed(2)})`; g2.shadowBlur = 16;
      g2.lineWidth = 2 + 5 * (1 - u); g2.strokeStyle = `rgba(${rgb},${al.toFixed(3)})`;
      ringPath(tvR.x - dx, tvR.y - du, tvR.w + 2 * dx, tvR.h + du + dd, 66 + (dx + du) / 2); g2.stroke();
    }
    g2.shadowBlur = 0; g2.shadowColor = "transparent"; g2.globalCompositeOperation = "source-over";
  }

  function applyScheme(v) {
    cur = v;
    cone.style.setProperty("--atv-c1", `rgb(${v.rgb[0].join(",")})`);
    cone.style.setProperty("--atv-c2", `rgb(${v.rgb[1].join(",")})`);
    cone.style.setProperty("--atv-c3", `rgb(${v.rgb[2].join(",")})`);
    atomRGB.a = v.atom[0]; atomRGB.b = v.atom[1];
    cone.style.setProperty("--atv-atom-a", v.atom[0].join(", ")); cone.style.setProperty("--atv-atom-b", v.atom[1].join(", "));
    cone.style.setProperty("--atv-gop", (v.glow / 100).toFixed(2));
    cone.style.setProperty("--atv-hp", `${v.style.indexOf("maypole") === 0 ? v.spin : v.per}s`);
    cone.setAttribute("data-halo", v.style);
    if (!v.beat || v.style === "none") endBoom();
    P.length = 0; fxAcc = []; batAcc = 0; g2.clearRect(0, 0, fxW, fxH);
    fxDef = reduce || !v.fx ? null : (FXDEF[v.fx] || null);
    if (fxDef && !fxRAF) { fxLast = performance.now(); fxRAF = raf(fxFrame); }
  }

  // ---------- the turn ----------
  const holds = { hover: false, touch: false, open: false, focus: false, video: false, zoom: false };
  let angle = 0, speed = 0, last = 0, forced = false;
  // "Spin while it plays" (the switch under the TV) lets the TV keep turning while a song plays on its screen
  let keepSpin = false;
  // zoom: the page is pinched in (a phone or tablet). The TV stands straight and holds still while someone looks closely: a turning picture that is
  // enlarged several times asks a phone's graphics for far more than it can give, and whole blocks of the picture go black.
  const wantStraight = () => reduce || holds.hover || holds.touch || holds.open || holds.focus || holds.zoom || (holds.video && !keepSpin);
  const offFront = () => Math.abs(angle - Math.round(angle / 360) * 360);

  // how far the corners swing out to the sides, with perspective, at this angle; if that is wider than the screen, the whole set is pulled back to fit
  function pullBack(deg) {
    const a = deg * Math.PI / 180, c = Math.cos(a), sn = Math.sin(a), pts = [[W0 / 2, D2], [-W0 / 2, D2], [0.4 * W0, -D2], [-0.4 * W0, -D2]];
    let ext = 0;
    for (let k = 0; k < 4; k++) {
      const x = pts[k][0] * c + pts[k][1] * sn, z = -pts[k][0] * sn + pts[k][1] * c, e = Math.abs(x) * PERSP / (PERSP - (z - D2));
      if (e > ext) ext = e;
    }
    return ext > LIMIT ? LIMIT / ext : 1;
  }
  function apply() {
    // Cinema mode holds ONE size for the whole turn (the best fit, worked out in layout); the usual shape pulls back a little at the angles that swing widest
    const sc = cinema ? scFix : pullBack(angle); curSc = sc;
    const tf = `scale(${sc.toFixed(4)}) translateZ(${(-D2).toFixed(1)}px) rotateY(${angle.toFixed(3)}deg)`;
    turn.style.transform = tf; turn2.style.transform = tf;
    if (glBody) glBody.render(angle, sc);
  }
  function frame(t) {
    const dt = Math.min(0.05, (t - last) / 1000); last = t;
    if (!forced) {
      if (wantStraight()) {
        speed = 0;
        if (offFront() < 0.5) angle = 0;
        else angle += (Math.round(angle / 360) * 360 - angle) * (1 - Math.exp(-dt * ((holds.open || holds.touch) ? 10 : 6)));
      } else { speed += (SPEED - speed) * (1 - Math.exp(-dt * 1.2)); angle -= speed * dt; }
      apply();
    }
    raf(frame);
  }

  // ---------- the atom: ONE atom lives in the middle of the set, drawn in 3-D, travelling a slow circle around the set's upright axis ----------
  // Every vent draws it as seen through that window by someone standing where the viewer is: the closer the atom is to a window, the bigger
  // and brighter it looks there. Frame: X to the right, Y down from the top, Z toward the front; origin at the middle of the body.
  const wins = [back.querySelector(".atv-atoms"), ...svs.map((e) => e.querySelector(".atv-atoms"))].map((el) => {
    const cv = el.querySelector("canvas");
    return { el, cv, ctx: cv.getContext("2d"), on: false };
  });
  const ATOM = { rho: 0, R: 0, cy: 0, eye: 0, ring: 0.3 };
  let atomDpr = 1, atomFix = null, atomN = 0;
  const atomT0 = performance.now();
  function winBack(H) {
    const bv = back.querySelector(".atv-vent"), w = bv.offsetWidth, h = bv.offsetHeight, k = 0.8, wn = wins[0];
    wn.w = w; wn.h = h; wn.k = k; wn.Tu = [-1, 0, 0]; wn.N = [0, 0, -1];
    wn.O = [-k * (bv.offsetLeft + w / 2 - W0 / 2), H + k * (bv.offsetTop + h / 2 - H), -D2];
  }
  function winSide(v, sgn, Lp, Hp, wallX, vTop, tilt) {
    const th = sgn * (90 + tilt) * Math.PI / 180, wn = wins[v + 1];
    wn.w = Lp; wn.h = Hp; wn.k = 1; wn.Tu = [Math.cos(th), 0, -Math.sin(th)]; wn.N = [Math.sin(th), 0, Math.cos(th)];
    wn.O = [sgn * (wallX + 1.5), vTop + Hp / 2 + belly(0.5), 0];
  }
  function atomSize(H) {
    // the atom is the same size in Cinema mode: it is sized from the usual 430 px width, not the wider cinema set (the vents it is seen through do not get bigger)
    const Wa = Math.min(W0, DESIGN_W);
    ATOM.rho = 0.2 * Wa; ATOM.R = 0.2 * Wa; ATOM.cy = 0.5 * H; ATOM.eye = 0.85 * Wa;
    atomDpr = Math.min(2, window.devicePixelRatio || 1) * Math.max(SC, 0.75);
    wins.forEach((wn) => {
      const cw = Math.max(1, Math.round(wn.w * atomDpr)), ch = Math.max(1, Math.round(wn.h * atomDpr));
      if (wn.cv.width !== cw || wn.cv.height !== ch) { wn.cv.width = cw; wn.cv.height = ch; }
    });
  }
  const DUR = [3.1, 4.3, 5.4];
  function drawAtom(wn, u, t) {
    const ctx = wn.ctx, O = wn.O, N3 = wn.N, Tu = wn.Tu, k = wn.k, W = wn.w, Hh = wn.h, Ed = ATOM.eye, R = ATOM.R;
    ctx.setTransform(atomDpr, 0, 0, atomDpr, 0, 0); ctx.clearRect(0, 0, W, Hh);
    const un = u[0] * N3[0] + u[1] * N3[1] + u[2] * N3[2];
    // the eye: out in front of this window, in the direction the viewer is
    const Ex = O[0] + u[0] * Ed, Ey = O[1] + u[1] * Ed, Ez = O[2] + u[2] * Ed, num = -Ed * un;
    // the atom's middle travels a circle in the level plane
    const ph = t * TWO / 9.5, Cx = ATOM.rho * Math.sin(ph), Cy = ATOM.cy, Cz = -ATOM.rho * Math.cos(ph);
    const proj = (x, y, z) => {
      const vx = x - Ex, vy = y - Ey, vz = z - Ez, den = vx * N3[0] + vy * N3[1] + vz * N3[2];
      let sS = num / den;
      if (!(sS > 0)) sS = 0.0001;
      if (sS > 1.6) sS = 1.6;
      const px = Ex + sS * vx - O[0], py = Ey + sS * vy - O[1], pz = Ez + sS * vz - O[2];
      return { x: (px * Tu[0] + pz * Tu[2]) / k + W / 2, y: py / k + Hh / 2, m: sS / k };
    };
    // how near the atom's middle is to this window: 1 close, about .35 across the set
    const dist = -((Cx - O[0]) * N3[0] + (Cy - O[1]) * N3[1] + (Cz - O[2]) * N3[2]), near = Math.max(0.4, Math.min(1, 1.3 - 0.85 * dist / (0.9 * W0)));
    const ca = atomRGB.a, cb = atomRGB.b, col = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
    const nuc = proj(Cx, Cy, Cz);
    // a soft glow around the nucleus
    const gr = 2.6 * R * nuc.m, g = ctx.createRadialGradient(nuc.x, nuc.y, 0, nuc.x, nuc.y, gr);
    g.addColorStop(0, col(cb, 0.42 * near)); g.addColorStop(0.5, col(ca, 0.16 * near)); g.addColorStop(1, col(ca, 0));
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, Hh);
    // the force field, the part of it behind the atom
    const fp = fieldPaths(proj, u, Cx, Cy, Cz);
    fieldStroke(ctx, fp.farB, fp.farP, 0.55, 0.55 + 0.45 * near);
    // the three orbits (they turn about the set's front-to-back line, once in 14 s) and two electrons on each
    const spin = t * TWO / 14, ct = ATOM.ring, st = Math.sqrt(1 - ATOM.ring * ATOM.ring), items = [], arcsB = [], arcsF = [];
    for (let i = 0; i < 3; i++) {
      const f = spin + i * Math.PI / 3, cf = Math.cos(f), sf = Math.sin(f);
      const e1 = [-sf, cf, 0], e2 = [-ct * cf, -ct * sf, st], pts = [], steps = 44;
      const at = (sa) => { const cs = Math.cos(sa), sn = Math.sin(sa); return [Cx + R * (cs * e1[0] + sn * e2[0]), Cy + R * (cs * e1[1] + sn * e2[1]), Cz + R * (cs * e1[2] + sn * e2[2])]; };
      for (let j = 0; j <= steps; j++) { const q = at(j / steps * TWO), pp = proj(q[0], q[1], q[2]); pp.d = (q[0] - Cx) * u[0] + (q[1] - Cy) * u[1] + (q[2] - Cz) * u[2]; pts.push(pp); }
      const pB = new Path2D(), fr = new Path2D();
      for (let j2 = 0; j2 < steps; j2++) { const A = pts[j2], B = pts[j2 + 1], pth = (A.d + B.d) < 0 ? pB : fr; pth.moveTo(A.x, A.y); pth.lineTo(B.x, B.y); }
      arcsB.push({ p: pB, c: i % 2 ? cb : ca, m: nuc.m }); arcsF.push({ p: fr, c: i % 2 ? cb : ca, m: nuc.m });
      for (let e = 0; e < 2; e++) {
        const se = TWO * (t / DUR[i] + e / 2 + i * 0.21), q2 = at(se), p2 = proj(q2[0], q2[1], q2[2]);
        items.push({ x: p2.x, y: p2.y, m: p2.m, d: (q2[0] - Cx) * u[0] + (q2[1] - Cy) * u[1] + (q2[2] - Cz) * u[2], c: (i + e) % 2 ? cb : ca });
      }
    }
    const arcs = (list, alpha) => list.forEach((a) => { ctx.lineWidth = Math.max(1, 2 * a.m * 1.1); ctx.strokeStyle = col(a.c, alpha * near); ctx.stroke(a.p); });
    const electron = (it) => {
      const sc = it.m * (1 + 0.14 * it.d / R), rc = Math.max(1.8, 4.6 * sc), rh = Math.max(4, 12 * sc), gg = ctx.createRadialGradient(it.x, it.y, 0, it.x, it.y, rh);
      gg.addColorStop(0, col(it.c, 0.6 * near)); gg.addColorStop(1, col(it.c, 0));
      ctx.fillStyle = gg; ctx.beginPath(); ctx.arc(it.x, it.y, rh, 0, TWO); ctx.fill();
      ctx.fillStyle = col(it.c, Math.min(1, 0.35 + near)); ctx.beginPath(); ctx.arc(it.x, it.y, rc, 0, TWO); ctx.fill();
      ctx.strokeStyle = `rgba(255,255,255,${(0.55 * near).toFixed(2)})`; ctx.lineWidth = 0.8; ctx.stroke();
    };
    items.sort((a, b) => a.d - b.d);
    arcs(arcsB, 0.5);
    items.forEach((it) => { if (it.d < 0) electron(it); });
    // the nucleus (pulses about every two seconds)
    const pulse = 1 + 0.1 * Math.sin(t * TWO / 2.2), nr = Math.max(2.5, 9 * nuc.m * pulse), nh = Math.max(6, 22 * nuc.m * pulse), ng = ctx.createRadialGradient(nuc.x, nuc.y, 0, nuc.x, nuc.y, nh);
    ng.addColorStop(0, col(cb, 0.7 * near)); ng.addColorStop(1, col(cb, 0));
    ctx.fillStyle = ng; ctx.beginPath(); ctx.arc(nuc.x, nuc.y, nh, 0, TWO); ctx.fill();
    ctx.fillStyle = col(cb, Math.min(1, 0.4 + near)); ctx.beginPath(); ctx.arc(nuc.x, nuc.y, nr, 0, TWO); ctx.fill();
    ctx.strokeStyle = `rgba(255,255,255,${(0.7 * near).toFixed(2)})`; ctx.lineWidth = 1; ctx.stroke();
    arcs(arcsF, 0.95);
    items.forEach((it) => { if (it.d >= 0) electron(it); });
    // the force field, the part of it in front of the atom
    fieldStroke(ctx, fp.nearB, fp.nearP, 1, 0.55 + 0.45 * near);
  }

  // ---------- the beam: a tractor beam rises from the round foot and holds the set up ----------
  // Same color as the atom's nucleus, and it breathes with the nucleus (every 2.2 s). Light rings climb it, three strands twist around it
  // (they turn with the set), and sparks rise.
  const BEAM = { w: 0, h: 0, dpr: 1, body: null, key: "" };
  function beamSize() {
    const cr = cone.getBoundingClientRect(), sr = slot.getBoundingClientRect(), fr = footEl.getBoundingClientRect();
    const topY = (sr.bottom - 0.04 * sr.height - cr.top) / SC, botY = (fr.top + fr.height * 0.44 - cr.top) / SC;
    BEAM.w = Math.round(cr.width / SC); BEAM.h = Math.max(20, Math.round(botY - topY)); BEAM.dpr = Math.min(1.5, window.devicePixelRatio || 1) * Math.max(SC, 0.75);
    beamCv.style.top = `${Math.round(topY)}px`; beamCv.style.width = `${BEAM.w}px`; beamCv.style.height = `${BEAM.h}px`;
    beamCv.width = Math.round(BEAM.w * BEAM.dpr); beamCv.height = Math.round(BEAM.h * BEAM.dpr); BEAM.key = "";
  }
  // half the beam's width at height s (0 at the top, under the set; 1 at the foot): wide under the TV, a narrow waist, flaring into the foot
  function beamHW(s) {
    const W = BEAM.w, Wn = Math.min(W, DESIGN_W), top = 0.25 * W, waist = Math.max(4, 0.045 * Wn), foot = 0.2 * Wn, wp = 0.55;   // the waist and the foot keep the usual width in Cinema mode (the round foot does not grow)
    return s < wp ? waist + (top - waist) * Math.pow((wp - s) / wp, 1.8) : waist + (foot - waist) * Math.pow((s - wp) / (1 - wp), 2.2);
  }
  function beamBody(c) {
    const W = BEAM.w, Hh = BEAM.h, d = BEAM.dpr, cv = document.createElement("canvas"); cv.width = Math.round(W * d); cv.height = Math.round(Hh * d);
    const x = cv.getContext("2d"); x.scale(d, d);
    const m = c.map((v) => Math.round(v + (255 - v) * 0.65));
    for (let y = 0; y < Hh; y += 1) {
      const s = y / Hh, hw = beamHW(s), f = (0.34 + 0.56 * s) * Math.min(1, 0.3 + s / 0.1), g = x.createLinearGradient(W / 2 - hw, 0, W / 2 + hw, 0);
      g.addColorStop(0, `rgba(${c},0)`); g.addColorStop(0.16, `rgba(${c},${(0.24 * f).toFixed(3)})`); g.addColorStop(0.36, `rgba(${c},${(0.5 * f).toFixed(3)})`);
      g.addColorStop(0.5, `rgba(${m},${(0.9 * f).toFixed(3)})`); g.addColorStop(0.64, `rgba(${c},${(0.5 * f).toFixed(3)})`);
      g.addColorStop(0.84, `rgba(${c},${(0.24 * f).toFixed(3)})`); g.addColorStop(1, `rgba(${c},0)`);
      x.fillStyle = g; x.fillRect(W / 2 - hw, y, hw * 2, 1.6);
    }
    return cv;
  }
  function drawBeam(t) {
    const ctx = beamCtx, W = BEAM.w, Hh = BEAM.h, d = BEAM.dpr, c = atomRGB.b, cx = W / 2, key = `${W}x${Hh}x${d}x${c}`;
    if (!W) return;
    if (BEAM.key !== key) { BEAM.body = beamBody(c); BEAM.key = key; }
    ctx.setTransform(d, 0, 0, d, 0, 0); ctx.clearRect(0, 0, W, Hh);
    const pulse = 0.8 + 0.2 * Math.sin(t * TWO / 2.2), col = (a) => `rgba(${c},${a})`, white = (a) => `rgba(255,255,255,${a})`;
    ctx.globalAlpha = pulse; ctx.drawImage(BEAM.body, 0, 0, W, Hh); ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "lighter";
    // where the beam leaves the foot, and where it meets the set
    const gb = ctx.createRadialGradient(cx, Hh, 0, cx, Hh, 0.26 * W); gb.addColorStop(0, col(0.55 * pulse)); gb.addColorStop(1, col(0));
    ctx.save(); ctx.translate(0, Hh); ctx.scale(1, 0.32); ctx.translate(0, -Hh); ctx.fillStyle = gb; ctx.fillRect(0, Hh - 0.3 * W, W, 0.6 * W); ctx.restore();
    const gt = ctx.createRadialGradient(cx, 0, 0, cx, 0, 0.3 * W); gt.addColorStop(0, col(0.35 * pulse)); gt.addColorStop(1, col(0));
    ctx.fillStyle = gt; ctx.fillRect(0, 0, W, 0.3 * W);
    // rings climbing the beam
    for (let k = 0; k < 5; k++) {
      const p = (t / 3.4 + k / 5) % 1, s = 1 - p, y = s * Hh, hw = beamHW(s) * 0.97, al = Math.pow(Math.sin(Math.PI * p), 0.8) * 0.75 * pulse;
      ctx.lineWidth = 1.3; ctx.strokeStyle = col(al * 0.45); ctx.beginPath(); ctx.ellipse(cx, y, hw, hw * 0.2, 0, 0, TWO); ctx.stroke();
      ctx.strokeStyle = white(al * 0.5); ctx.beginPath(); ctx.ellipse(cx, y, hw, hw * 0.2, 0, 0.05 * Math.PI, 0.95 * Math.PI); ctx.stroke();
    }
    // three strands twisting up the beam, in the color of the atom's other electrons (the blue ones); they turn with the set
    const colA = (a) => `rgba(${atomRGB.a},${a})`, turnA = -angle * Math.PI / 180 + t * 1.4;
    for (let q = 0; q < 3; q++) {
      const fr = new Path2D(), bk = new Path2D(), ph = q * TWO / 3;
      let pf = null;
      for (let y2 = 0; y2 <= Hh; y2 += 4) {
        const s2 = y2 / Hh, a2 = ph + s2 * TWO * 1.5 + turnA, x2 = cx + beamHW(s2) * 0.82 * Math.sin(a2), dp = Math.cos(a2);
        if (pf) { const path = (dp + pf.d) < 0 ? bk : fr; path.moveTo(pf.x, pf.y); path.lineTo(x2, y2); }
        pf = { x: x2, y: y2, d: dp };
      }
      ctx.lineWidth = 1; ctx.strokeStyle = colA(0.35 * pulse); ctx.stroke(bk);
      ctx.lineWidth = 1.7; ctx.strokeStyle = colA(0.9 * pulse); ctx.stroke(fr);
      ctx.lineWidth = 0.7; ctx.strokeStyle = white(0.45 * pulse); ctx.stroke(fr);
    }
    // sparks rising
    for (let j = 0; j < 18; j++) {
      const h1 = (j * 0.6180339) % 1, h2 = (j * 0.7548777) % 1, pj = (t * (0.1 + 0.12 * h2) + h1) % 1, sj = 1 - pj, xj = cx + beamHW(sj) * (h1 * 1.6 - 0.8) * 0.9 + Math.sin(t * 2 + j) * 1.5;
      ctx.fillStyle = white(Math.sin(Math.PI * pj) * 0.8); ctx.beginPath(); ctx.arc(xj, sj * Hh, 0.8 + 1.2 * h2, 0, TWO); ctx.fill();
    }
    ctx.globalCompositeOperation = "source-over";
  }

  // ---------- the force field: a lattice of hoops, rails and X-braces, 10 px inside the shell, that holds the set together against the spin ----------
  // Its colors are the stand's, reversed: a blue lattice (the atom's blue electrons) with pink strands (the nucleus pink) winding through it.
  // It is drawn twice: on a canvas under the body (so it shows through the notch where the body is edge-on) and through each vent, from the viewer's side.
  const fieldCv = q(".atv-field"), fieldCtx = fieldCv.getContext("2d");
  const FF = { style: "diamond", ins: 10, segs: new Float32Array(0), nodes: new Float32Array(0), str: [], gain: 1, W: 0, hh: 0, H: 0, ox: 0, oy: 0, dpr: 1, shown: false };
  // a rounded rectangle walked clockwise from the middle of its top side; u runs 0..1 around it. x is from the middle, y from the top of the rectangle
  function rrAt(u, w, h, rt, rb) {
    const hw = w / 2, H2 = Math.PI / 2, L = [hw - rt, H2 * rt, h - rt - rb, H2 * rb, w - 2 * rb, H2 * rb, h - rt - rb, H2 * rt, hw - rt];
    let tot = 0, i;
    for (i = 0; i < 9; i++) tot += L[i];
    let s = (((u % 1) + 1) % 1) * tot;
    for (i = 0; i < 9 && s > L[i]; i++) s -= L[i];
    if (i > 8) { i = 8; s = L[8]; }
    const f = L[i] > 0 ? s / L[i] : 0;
    let a;
    switch (i) {
      case 0: return [f * (hw - rt), 0];
      case 1: a = -H2 + f * H2; return [hw - rt + rt * Math.cos(a), rt + rt * Math.sin(a)];
      case 2: return [hw, rt + f * (h - rt - rb)];
      case 3: a = f * H2; return [hw - rb + rb * Math.cos(a), h - rb + rb * Math.sin(a)];
      case 4: return [hw - rb - f * (w - 2 * rb), h];
      case 5: a = H2 + f * H2; return [-hw + rb + rb * Math.cos(a), h - rb + rb * Math.sin(a)];
      case 6: return [-hw, h - rb - f * (h - rt - rb)];
      case 7: a = Math.PI + f * H2; return [-hw + rt + rt * Math.cos(a), rt + rt * Math.sin(a)];
      default: return [-hw + rt + f * (hw - rt), 0];
    }
  }
  // The cage is a skin 10 px inside the shell, with a pattern drawn on each of its six sides: the four long sides (top, bottom, left and
  // right, with the rounded corners between them) and the front and the back. The look (FF.style) is the pattern: "diamond" (criss-crossing
  // diagonals), "octagon" (eight-sided cells) or "grid" (squares). The person picks one with the switch under the TV.
  const FF_T0 = 0.06, FF_T1 = 0.94;
  // how many cells: [columns on each flat side (and one on each rounded corner), rows from the front to the back]
  const FF_DENSITY = { diamond: [5, 6], octagon: [5, 5], grid: [6, 7] };
  const FF_OCT = 1 / (2 + Math.SQRT2);       // the corner cut of a regular octagon, as a part of its cell
  // the cross-section of the cage at depth t (0 = front, 1 = back): the shell's own slice, 10 px smaller all round
  function ffGeo(t, H) {
    const wd = wid(t), ins = FF.ins, w = W0 * wd - 2 * ins, h = H * wd - 2 * ins, yb = H + belly(t) - ins;
    return { w, h, rt: Math.max(4, 64 * wd - ins), rb: Math.max(4, 84 * wd - ins), yt: yb - h, z: D2 - t * D };
  }
  function ffPt(t, u, H, g0) {
    const g = g0 || ffGeo(t, H), p = rrAt(u, g.w, g.h, g.rt, g.rb);
    return [p[0], g.yt + p[1], g.z];
  }
  // where along the perimeter (0..1) the eight corners (A) of the four flat sides are
  function ffKeys(g) {
    const H2 = Math.PI / 2, hw = g.w / 2, L = [hw - g.rt, H2 * g.rt, g.h - g.rt - g.rb, H2 * g.rb, g.w - 2 * g.rb, H2 * g.rb, g.h - g.rt - g.rb, H2 * g.rt, hw - g.rt], c = [0];
    let tot = 0;
    for (let i = 0; i < 9; i++) { tot += L[i]; c.push(tot); }
    const at = (k) => c[k] / tot;
    return { A: [at(1), at(2), at(3), at(4), at(5), at(6), at(7), at(8)] };
  }
  // where each column of the belt starts (21 places round the cage, counting from the top side's left end; the last is the first plus one)
  function ffCols(ky, flat) {
    const A = ky.A, B = [A[7] - 1, A[0], A[1], A[2], A[3], A[4], A[5], A[6], A[7]], out = [];
    for (let i = 0; i < 8; i++) {
      const n = i % 2 === 0 ? flat : 1;      // the top, right, bottom and left sides have several columns; each rounded corner one
      for (let k = 0; k < n; k++) out.push(B[i] + (B[i + 1] - B[i]) * k / n);
    }
    out.push(B[8]);
    return out;
  }
  // the lines of one cell of the pattern, as [a0, b0, a1, b1] in the cell's own square (a along the belt, b from the front toward the back).
  // A line shared with the next cell is drawn once, by the cell it starts in.
  function ffCell(style, firstRow, lastRow, lastCol) {
    const c = FF_OCT, e = [];
    if (style === "grid") {
      e.push([0, 0, 1, 0], [0, 0, 0, 1]);
      if (lastRow) e.push([0, 1, 1, 1]);
      if (lastCol) e.push([1, 0, 1, 1]);
    } else if (style === "octagon") {
      e.push([c, 0, 1 - c, 0], [0, c, 0, 1 - c], [c, 0, 0, c], [1 - c, 0, 1, c], [1, 1 - c, 1 - c, 1], [0, 1 - c, c, 1]);
      if (lastRow) e.push([c, 1, 1 - c, 1]);
      if (lastCol) e.push([1, c, 1, 1 - c]);
    } else {
      e.push([0, 0, 1, 1], [1, 0, 0, 1]);
      if (firstRow) e.push([0, 0, 1, 0]);
      if (lastRow) e.push([0, 1, 1, 1]);
    }
    return e;
  }
  // a line from p to q cut to a convex outline (a list of [x, y] points); gives [p, q] or null when nothing is left
  function ffClip(p, q, poly) {
    const n = poly.length, dx = q[0] - p[0], dy = q[1] - p[1];
    let area = 0, t0 = 0, t1 = 1;
    for (let i = 0; i < n; i++) { const a0 = poly[i], b0 = poly[(i + 1) % n]; area += a0[0] * b0[1] - b0[0] * a0[1]; }
    const sg = area >= 0 ? 1 : -1;
    for (let i = 0; i < n; i++) {
      const a0 = poly[i], b0 = poly[(i + 1) % n], ex = b0[0] - a0[0], ey = b0[1] - a0[1];
      const num = sg * (ex * (p[1] - a0[1]) - ey * (p[0] - a0[0])), den = sg * (ex * dy - ey * dx);
      if (den === 0) { if (num < 0) return null; } else {
        const t = -num / den;
        if (den > 0) { if (t > t0) t0 = t; } else if (t < t1) t1 = t;
        if (t0 > t1) return null;
      }
    }
    return [[p[0] + dx * t0, p[1] + dy * t0], [p[0] + dx * t1, p[1] + dy * t1]];
  }
  function ffBuild(H) {
    FF.ins = 10 * Math.min(W0, DESIGN_W) / DESIGN_W;
    const style = FF.style, dens = FF_DENSITY[style] || FF_DENSITY.diamond, flat = dens[0], nc = 4 * flat + 4, rows = dens[1], ts = [], segs = [], nodes = [];
    for (let r = 0; r <= rows; r++) ts.push(FF_T0 + (FF_T1 - FF_T0) * r / rows);
    const add = (a0, b0) => segs.push(a0[0], a0[1], a0[2], b0[0], b0[1], b0[2]);
    // a point inside cell (row r, column c) of the belt: a along the belt, b from the front toward the back (0..1)
    const cellPt = (r, c, a, b) => {
      const t = ts[r] + (ts[r + 1] - ts[r]) * b, g = ffGeo(t, H), cu = ffCols(ffKeys(g), flat);
      return ffPt(t, cu[c] + (cu[c + 1] - cu[c]) * a, H, g);
    };
    // the four long sides and the rounded corners: one belt of cells
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < nc; c++) {
        const corner = c % (flat + 1) === flat;      // a rounded corner: its lines are drawn in pieces so they follow the curve
        ffCell(style, r === 0, r === rows - 1, false).forEach((l) => {
          const n = Math.max(corner ? 3 : 1, Math.abs(l[3] - l[1]) > 0.6 ? 2 : 1);
          let pr = cellPt(r, c, l[0], l[1]);
          for (let k = 1; k <= n; k++) { const q2 = cellPt(r, c, l[0] + (l[2] - l[0]) * k / n, l[1] + (l[3] - l[1]) * k / n); add(pr, q2); pr = q2; }
        });
      }
    }
    // the front and the back: the same pattern laid flat and cut to the outline
    [FF_T0, FF_T1].forEach((t) => {
      const g = ffGeo(t, H), M = 40, poly = [];
      for (let m = 0; m < M; m++) { const q2 = ffPt(t, m / M, H, g); poly.push([q2[0], q2[1]]); }
      for (let m = 0; m < M; m++) { const p1 = poly[m], p2 = poly[(m + 1) % M]; add([p1[0], p1[1], g.z], [p2[0], p2[1], g.z]); }
      const nx = flat + 1, cw = g.w / nx, ny = Math.max(3, Math.round(g.h / cw)), ch = g.h / ny;
      for (let j = 0; j < ny; j++) {
        for (let i = 0; i < nx; i++) {
          ffCell(style, j === 0, j === ny - 1, i === nx - 1).forEach((l) => {
            const cl = ffClip([-g.w / 2 + (i + l[0]) * cw, g.yt + (j + l[1]) * ch], [-g.w / 2 + (i + l[2]) * cw, g.yt + (j + l[3]) * ch], poly);
            if (cl) add([cl[0][0], cl[0][1], g.z], [cl[1][0], cl[1][1], g.z]);
          });
        }
      }
    });
    // nodes: a dot at every corner of the belt's cells
    for (let r = 0; r <= rows; r++) {
      const g = ffGeo(ts[r], H), cu = ffCols(ffKeys(g), flat);
      for (let c = 0; c < nc; c++) { const q2 = ffPt(ts[r], cu[c], H, g); nodes.push(q2[0], q2[1], q2[2]); }
    }
    FF.segs = new Float32Array(segs); FF.nodes = new Float32Array(nodes); FF.H = H;
    // the canvas under the body: wide enough for the set turned side-on, a little taller than the body
    // (on a phone the set is as wide as the window, so the canvas is trimmed to stay inside the window: it must not make the page slide sideways)
    const dpr = Math.min(1.5, window.devicePixelRatio || 1) * Math.max(SC, 0.75), padX = 0.2 * W0, padT = 0.1 * H, padB = 0.2 * H;
    let padL = padX, padR = padX;
    const host = fieldCv.offsetParent, hr = host ? host.getBoundingClientRect() : null, vw = document.documentElement.clientWidth || window.innerWidth;
    if (hr && Math.abs(hr.width / SC - W0) < 3) { padL = Math.max(0, Math.min(padX, hr.left / SC)); padR = Math.max(0, Math.min(padX, (vw - hr.right) / SC)); }
    FF.ox = padL; FF.oy = padT; FF.dpr = dpr; FF.W = W0 + padL + padR; FF.hh = H + padT + padB;
    fieldCv.style.left = `${-padL}px`; fieldCv.style.top = `${-padT}px`; fieldCv.style.width = `${FF.W}px`; fieldCv.style.height = `${FF.hh}px`;
    fieldCv.width = Math.round(FF.W * dpr); fieldCv.height = Math.round(FF.hh * dpr); FF.shown = false;
  }
  // the three pink strands: they wind around the cage from the front to the back and slide along it, like cables carrying the load
  function ffStrands(t) {
    const H = FF.H, out = [];
    for (let q = 0; q < 3; q++) {
      const pts = [];
      for (let m = 0; m <= 30; m++) { const tt = 0.06 + 0.88 * m / 30, pp = ffPt(tt, q / 3 + 1.25 * tt + t * 0.045, H); pts.push(pp[0], pp[1], pp[2]); }
      out.push(pts);
    }
    FF.str = out;
  }
  const ffRgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${Math.max(0, Math.min(1, a)).toFixed(3)})`;
  // the body turns about the same axis as the CSS does; this gives where a point of the set lands on the page (relative to the set's own box) and how near it is
  function ffProject(x, y, z, c, sn) {
    const xr = x * c + z * sn, zr = -x * sn + z * c, k = PERSP / (PERSP - (zr - D2));
    return [W0 / 2 + xr * curSc * k, FF.H / 2 + ((y - FF.H) * curSc + FF.H / 2) * k, zr];
  }
  function ffMain() {
    const ctx = fieldCtx, a = angle * Math.PI / 180;
    // only the side views have a notch to look through, so the rest of the time nothing is drawn
    if (Math.abs(Math.sin(a)) < 0.78) {
      if (FF.shown) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, fieldCv.width, fieldCv.height); FF.shown = false; }
      return;
    }
    FF.shown = true;
    const c = Math.cos(a), sn = Math.sin(a), d = FF.dpr, ox = FF.ox, oy = FF.oy, s = FF.segs;
    ctx.setTransform(d, 0, 0, d, 0, 0); ctx.clearRect(0, 0, FF.W, FF.hh);
    const ca = atomRGB.a, cb = atomRGB.b, g = FF.gain, far = new Path2D(), mid = new Path2D(), near = new Path2D(), pf = new Path2D(), pn = new Path2D();
    const pick = (zr) => { const n = (zr + D2) / (2 * D2); return n < 0.34 ? far : n < 0.67 ? mid : near; };
    let p1, p2, i;
    for (i = 0; i < s.length; i += 6) {
      p1 = ffProject(s[i], s[i + 1], s[i + 2], c, sn); p2 = ffProject(s[i + 3], s[i + 4], s[i + 5], c, sn);
      const pth = pick((p1[2] + p2[2]) / 2); pth.moveTo(p1[0] + ox, p1[1] + oy); pth.lineTo(p2[0] + ox, p2[1] + oy);
    }
    FF.str.forEach((st) => {
      for (let j = 0; j + 5 < st.length; j += 3) {
        const q1 = ffProject(st[j], st[j + 1], st[j + 2], c, sn), q2 = ffProject(st[j + 3], st[j + 4], st[j + 5], c, sn);
        const pt = (q1[2] + q2[2]) / 2 > 0 ? pn : pf; pt.moveTo(q1[0] + ox, q1[1] + oy); pt.lineTo(q2[0] + ox, q2[1] + oy);
      }
    });
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    [[far, 0.14, 0.8], [mid, 0.5, 1.1], [near, 1, 1.7]].forEach((L) => {
      ctx.lineWidth = 4.5; ctx.strokeStyle = ffRgba(ca, 0.09 * L[1] * g); ctx.stroke(L[0]);
      ctx.lineWidth = L[2]; ctx.strokeStyle = ffRgba(ca, 0.8 * L[1] * g); ctx.stroke(L[0]);
    });
    ctx.lineWidth = 5; ctx.strokeStyle = ffRgba(cb, 0.08 * g); ctx.stroke(pf); ctx.stroke(pn);
    ctx.lineWidth = 1.2; ctx.strokeStyle = ffRgba(cb, 0.45 * g); ctx.stroke(pf);
    ctx.lineWidth = 1.7; ctx.strokeStyle = ffRgba(cb, 0.95 * g); ctx.stroke(pn);
    // a small bright node where each rail crosses a hoop
    const nd = FF.nodes, np = new Path2D();
    for (i = 0; i < nd.length; i += 3) {
      p1 = ffProject(nd[i], nd[i + 1], nd[i + 2], c, sn);
      if (p1[2] > -D2 * 0.4) { np.moveTo(p1[0] + ox + 1.8, p1[1] + oy); np.arc(p1[0] + ox, p1[1] + oy, 1.8, 0, TWO); }
    }
    ctx.fillStyle = `rgba(255,255,255,${(0.75 * g).toFixed(2)})`; ctx.fill(np);
  }
  // the same cage as a vent shows it: every segment goes through that window's own view (proj), and is drawn behind the atom or in front of it
  function fieldPaths(proj, u, Cx, Cy, Cz) {
    const out = { farB: new Path2D(), nearB: new Path2D(), farP: new Path2D(), nearP: new Path2D() }, s = FF.segs;
    for (let i = 0; i < s.length; i += 6) {
      const d = ((s[i] + s[i + 3]) / 2 - Cx) * u[0] + ((s[i + 1] + s[i + 4]) / 2 - Cy) * u[1] + ((s[i + 2] + s[i + 5]) / 2 - Cz) * u[2];
      const a = proj(s[i], s[i + 1], s[i + 2]), b = proj(s[i + 3], s[i + 4], s[i + 5]), pth = d < 0 ? out.farB : out.nearB;
      pth.moveTo(a.x, a.y); pth.lineTo(b.x, b.y);
    }
    FF.str.forEach((st) => {
      for (let i = 0; i + 5 < st.length; i += 3) {
        const d = ((st[i] + st[i + 3]) / 2 - Cx) * u[0] + ((st[i + 1] + st[i + 4]) / 2 - Cy) * u[1] + ((st[i + 2] + st[i + 5]) / 2 - Cz) * u[2];
        const a = proj(st[i], st[i + 1], st[i + 2]), b = proj(st[i + 3], st[i + 4], st[i + 5]), pth = d < 0 ? out.farP : out.nearP;
        pth.moveTo(a.x, a.y); pth.lineTo(b.x, b.y);
      }
    });
    return out;
  }
  function fieldStroke(ctx, pb, pp, k, near) {
    const g = FF.gain * near, ca = atomRGB.a, cb = atomRGB.b;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (k >= 1) { ctx.lineWidth = 4; ctx.strokeStyle = ffRgba(ca, 0.07 * g); ctx.stroke(pb); ctx.lineWidth = 4.5; ctx.strokeStyle = ffRgba(cb, 0.09 * g); ctx.stroke(pp); }   // the glow only on the near lines
    ctx.lineWidth = 1.1; ctx.strokeStyle = ffRgba(ca, 0.7 * k * g); ctx.stroke(pb);
    ctx.lineWidth = 1.5; ctx.strokeStyle = ffRgba(cb, 0.9 * k * g); ctx.stroke(pp);
  }

  let atomLast = 0;
  function atomFrame(now) {
    raf(atomFrame);
    // the atom is redrawn about 30 times a second: smooth enough, and it keeps the turning set light on slower phones
    if (now - atomLast < 30 && atomFix === null) return;
    atomLast = now; atomN++;
    const t = atomFix !== null ? atomFix : (reduce ? 0 : (now - atomT0) / 1000), a = angle * Math.PI / 180, u = [-Math.sin(a), 0, Math.cos(a)];
    drawBeam(t);
    FF.gain = (0.62 + 0.38 * Math.min(1, speed / SPEED)) * (0.85 + 0.15 * Math.sin(t * TWO / 2.2 + Math.PI));
    ffStrands(t); ffMain();
    for (let i = 0; i < wins.length; i++) {
      const wn = wins[i];
      if (!wn.O) continue;
      if (u[0] * wn.N[0] + u[1] * wn.N[1] + u[2] * wn.N[2] > 0.04) { drawAtom(wn, u, t); wn.on = true; }
      else if (wn.on) { wn.ctx.setTransform(1, 0, 0, 1, 0, 0); wn.ctx.clearRect(0, 0, wn.cv.width, wn.cv.height); wn.on = false; }
    }
  }

  // ---------- the sunken parts (3-D body only) ----------
  // The screen frame sits SCREEN_Q px back inside the front (the body is cut away around it, with a rounded lip and a gently curved glass), and the ports sit IO_Q px back
  // inside the rear panel. The page's own screen and ports are moved back by the same amount (CSS translate), so they stay in step with the cut-away at every angle.
  // This reads where each part is on its face, in the face's own pixels, and gives the 3-D body the same places to cut.
  const SCREEN_Q = 26, IO_Q = 12, LOGO_Q = 4, RING = 10;
  const PODR = [64, 64, 84, 84];
  const rrPath = (x, y, w, h, tl, tr, br, bl) => `M${x + tl},${y}H${x + w - tr}A${tr},${tr} 0 0 1 ${x + w},${y + tr}V${y + h - br}A${br},${br} 0 0 1 ${x + w - br},${y + h}H${x + bl}A${bl},${bl} 0 0 1 ${x},${y + h - bl}V${y + tl}A${tl},${tl} 0 0 1 ${x + tl},${y}Z`;
  const r1 = (n) => Math.round(n * 10) / 10;
  const radiusOf = (el, d) => { const v = el ? parseFloat(getComputedStyle(el).borderTopLeftRadius) : NaN; return Number.isFinite(v) ? v : d; };
  function glPanels() {
    const out = { front: { face: "dom", holes: [], windows: [] }, back: { holes: [], windows: [] } };
    const bz = front.querySelector(".atv-bezel");
    if (bz && bz.offsetWidth > 0) {
      const sc = bz.querySelector(".atv-screen"), x = bz.offsetLeft, y = bz.offsetTop, w = bz.offsetWidth, h = bz.offsetHeight, r = radiusOf(bz, 38);
      const pad = parseFloat(getComputedStyle(bz).paddingLeft) || 10;
      out.front.holes.push({ x: r1(x - RING), y: r1(y - RING), w: r1(w + 2 * RING), h: r1(h + 2 * RING), r: r1(r + RING), fx: r1(x), fy: r1(y), fw: r1(w), fh: r1(h), fr: r1(r),
        depth: SCREEN_Q, lip: 3.4, glass: 7, gx: r1(x + pad), gy: r1(y + pad), gw: r1(w - 2 * pad), gh: r1(h - 2 * pad), gr: r1(radiusOf(sc, 28)) });
      // the page's own face: a hole cut in its plate, as big as the opening, so the sunken frame shows through
      const pw = bz.offsetParent ? bz.offsetParent.offsetWidth : W0, ph = bz.offsetParent ? bz.offsetParent.offsetHeight : turn.offsetHeight;
      const path = `path(evenodd, "${rrPath(0, 0, pw, ph, ...PODR)} ${rrPath(x - RING, y - RING, w + 2 * RING, h + 2 * RING, r + RING, r + RING, r + RING, r + RING)}")`;
      front.querySelectorAll(".atv-plate").forEach((pl) => pl.style.setProperty("--atv-hole", path));
    }
    const bv = back.querySelector(".atv-vent"), io = back.querySelector(".atv-io"), bd = back.querySelector(".atv-badge");
    if (bv) out.back.windows.push({ x: r1(bv.offsetLeft - 3.5), y: r1(bv.offsetTop - 3.5), w: r1(bv.offsetWidth + 7), h: r1(bv.offsetHeight + 7), r: 15.5 });
    // the logo plate sits a little way back too (4 px), in a snug opening
    if (bd) out.back.holes.push({ x: r1(bd.offsetLeft - 1.5), y: r1(bd.offsetTop - 1.5), w: r1(bd.offsetWidth + 3), h: r1(bd.offsetHeight + 3), r: r1(bd.offsetHeight / 2 + 1.5),
      fx: r1(bd.offsetLeft), fy: r1(bd.offsetTop), fw: r1(bd.offsetWidth), fh: r1(bd.offsetHeight), fr: r1(bd.offsetHeight / 2), depth: LOGO_Q, lip: 0, glass: 0 });
    if (io) out.back.holes.push({ x: r1(io.offsetLeft - 2.5), y: r1(io.offsetTop - 2.5), w: r1(io.offsetWidth + 5), h: r1(io.offsetHeight + 5), r: 9.5,
      fx: r1(io.offsetLeft), fy: r1(io.offsetTop), fw: r1(io.offsetWidth), fh: r1(io.offsetHeight), fr: 7, depth: IO_Q, lip: 0, glass: 0 });
    return out;
  }

  // ---------- layout: sizes and places, redone whenever the window changes ----------
  function layout() {
    W0 = turn.offsetWidth; D = Math.min(1.03 * W0, 560); D2 = D / 2;
    PERSP = Math.max(900, Math.round(PERSP_PER_W * W0)); slot.style.perspective = `${PERSP}px`;
    // how far from the middle the swinging TV may reach: to the edge of the stage (the page keeps a margin at each side and clips what goes past it), not to the window's edge
    const winW = document.documentElement.clientWidth || window.innerWidth;
    LIMIT = (Math.min(winW, root.clientWidth || winW) / 2 - 4) / SC;
    // Cinema mode: the TV is so wide that it would pull back by a different amount at every angle, which looks like it is resizing as it spins. Instead take the
    // tightest angle once and hold that one size for the whole turn (the best fit).
    scFix = 1;
    if (cinema) for (let deg = 0; deg < 360; deg += 3) scFix = Math.min(scFix, pullBack(deg));
    front.style.transform = `translateZ(${D2.toFixed(1)}px)`;
    front.style.setProperty("--atv-q", `${SCREEN_Q}px`); back.style.setProperty("--atv-q", `${IO_Q}px`);
    back.style.transform = `translateZ(${(-D2).toFixed(1)}px) rotateY(180deg) scale(.8)`;
    const H = turn.offsetHeight; BELLY = 0.075 * H;
    // how many slices: enough that seen from the side there are no gaps between them (about one every 4 device pixels), but on a phone no more
    // than its graphics memory can hold (each slice is its own layer); a zoomed-in computer window is judged by its device pixels like any other
    if (!glOn) {
      const dpr = (window.devicePixelRatio || 1) * SC, coarse = touchDevice();
      const byGap = Math.ceil(D * dpr / 4), byMem = Math.floor((coarse ? 80e6 : 480e6) / Math.max(1, W0 * H * dpr * dpr * 4));
      const want = Math.max(coarse ? 56 : 40, Math.min(MAX_SLICES, byGap, byMem));
      if (want !== shown) {
        shown = want; N = want; slices = allSlices.slice(0, N);
        allSlices.forEach((el, k) => { if (k < N) { el.style.display = ""; el.style.setProperty("--atv-a", sliceAlpha(k, N)); } else el.style.display = "none"; });
      }
    }
    // streamlined profile: the sides curve in smoothly from the front (no corner), and the underside swells into a belly
    for (let k = 0; k < N; k++) { const t = (k + 1) / (N + 1); slices[k].style.transform = `translate3d(0,${belly(t).toFixed(2)}px,${(D2 - t * D).toFixed(2)}px) scale(${wid(t).toFixed(4)})`; }
    // Seen from the side, a stack of thin slices turns into separate sticks with gaps between them. Two solid walls inside the body, a hair under its
    // sides (6 flat pieces each, following the curve, cut to the body's outline from the side), fill those gaps with the same wood. They are out of sight at every
    // other angle, hidden inside the slices.
    if (!glOn) {
      const Hb = Math.ceil(H + BELLY + 2), sgnOf = (i) => (i < WALL_SEGS ? 1 : -1);
      // The walls start at the first slice and stop at the last one (a hair inside them), not at the front and back faces: seen from the side a face is
      // paper thin, so a wall that reached it would hang out past the body's front and back edges.
      const tLo = 1 / (N + 1) + 1.5 / D, tHi = N / (N + 1) - 1.5 / D;
      // Only on a computer with a mouse (not an iPad that says it has one). The walls cut through the slices, and on a real phone (checked on Dan's Samsung, 2026-10-22)
      // that makes the graphics drop whole bands of slices and cover the front screen; a phone, tablet or TV does without them, like the TV before the walls were added.
      const wallsOn = !touchDevice() && !!(window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches);
      walls.forEach((el, i) => {
        el.style.display = wallsOn ? "" : "none";
        if (!wallsOn) return;
        const sgn = sgnOf(i), j = i % WALL_SEGS, ta = tLo + (tHi - tLo) * j / WALL_SEGS, tb = tLo + (tHi - tLo) * (j + 1) / WALL_SEGS, tm = (ta + tb) / 2;
        const dx = 0.5 * W0 * (wid(ta) - wid(tb)), dz = D * (tb - ta), L = Math.hypot(dx, dz) + 1.5, tilt = Math.atan2(dx, dz) * 180 / Math.PI;
        const xm = 0.5 * W0 * (wid(ta) + wid(tb)) / 2 - 1.5, zm = D2 - tm * D;
        // only the straight part of the side: the rounded top and bottom corners of the body are left to the slices, so the walls never poke out past them
        const top = (t) => (belly(t) + H * (1 - wid(t)) + 51.2 * wid(t) + 3).toFixed(1), bot = (t) => (belly(t) + H - 69 * wid(t) - 3).toFixed(1);
        // the right wall's left end is the front of the set, the left wall's left end is the back
        const [a, b] = sgn > 0 ? [ta, tb] : [tb, ta];
        el.style.width = `${L.toFixed(1)}px`; el.style.height = `${Hb}px`; el.style.marginLeft = `${(-L / 2).toFixed(1)}px`;
        el.style.clipPath = `polygon(0px ${top(a)}px, ${L.toFixed(1)}px ${top(b)}px, ${L.toFixed(1)}px ${bot(b)}px, 0px ${bot(a)}px)`;
        el.style.transform = `translate3d(${(sgn * xm).toFixed(2)}px,0,${zm.toFixed(2)}px) rotateY(${(sgn * (90 + tilt)).toFixed(2)}deg)`;
        el.style.setProperty("--atv-wa", (0.05 + 0.5 * a).toFixed(3)); el.style.setProperty("--atv-wb", (0.05 + 0.5 * b).toFixed(3));
      });
    }
    // the stand stands under the TV's turning axis, which is D2 farther back than the front of the TV: draw it that much smaller, so it meets the set
    const Lb = Math.round(0.46 * Math.min(W0, DESIGN_W)) - 15;      // the stand is the same length in Cinema mode
    standEl.style.marginTop = `${Lb + BELLY - 4 - 10}px`;
    standEl.style.transformOrigin = `50% ${(-(H / 2 + Lb + BELLY - 4 - 10)).toFixed(1)}px`;
    standEl.style.transform = `scale(${(PERSP / (PERSP + D2)).toFixed(4)})`;
    // the side vents lie along the sloping sides, a hair outside them
    const tilt = Math.atan2(0.1571 * W0 * Math.sin(Math.PI / 4), D) * 180 / Math.PI, Lp = Math.round(D * 0.46), Hp = 92, wallX = 0.5 * W0 * wid(0.5);
    svs.forEach((sv, v) => {
      const sgn = v === 0 ? -1 : 1, vTop = Math.round(0.5 * H + 6 - 0.2 * H);   // raised by a fifth of the TV's height
      sv.style.width = `${Lp}px`; sv.style.height = `${Hp}px`; sv.style.marginLeft = `${-Lp / 2}px`; sv.style.top = `${vTop}px`;
      sv.style.transform = `translate3d(${(sgn * (wallX + 1.5)).toFixed(2)}px,${belly(0.5).toFixed(1)}px,0) rotateY(${(sgn * (90 + tilt)).toFixed(2)}deg)`;
      winSide(v, sgn, Lp, Hp, wallX, vTop, tilt);
    });
    if (glBody) glBody.setSpec({ W0, H, D, BELLY, PERSP, wid, belly, panels: glPanels(), dpr: Math.min(2, window.devicePixelRatio || 1) * Math.max(SC, 0.75) });
    winBack(H); atomSize(H); beamSize(); ffBuild(H);
    apply();
    fxSize();
  }
  // fit: the TV and its stand together are centered top to bottom in the window, with room above and below.
  // If the window is short, the whole set is made smaller to keep that room.
  // the small player bar along the bottom of the window (while a song plays) covers the foot of the page, so the set is sized to fit above it
  const miniH = () => { const m = document.querySelector('[data-testid="mini-player"]'); return m ? Math.round(m.getBoundingClientRect().height) : 0; };
  let reserved = 0;
  // How much of the window a person can really see. A phone's browser can have a toolbar over the bottom of the window; the "small viewport" height (100svh)
  // is the window with those bars showing, so the TV is sized to that and its buttons are never hidden under a bar.
  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText = "position:fixed;left:0;top:0;width:0;height:100svh;visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  offs.push(() => probe.remove());
  const visibleH = () => {
    // the pinch-zoomed view is smaller than the window by exactly the zoom: the zoom is taken back out, so zooming in does not make the TV look short of room
    const v = window.visualViewport, a = window.innerHeight || document.documentElement.clientHeight, vv = v && v.height > 0 ? v.height * (v.scale || 1) : 0, sv = probe.getBoundingClientRect().height;
    return Math.round(Math.min(a, vv > 0 ? vv : a, sv > 0 ? sv : a));
  };
  // pinched in on a phone or tablet (the page's own zoom, not a computer's Ctrl+scroll, which leaves visualViewport.scale alone)
  const pinched = () => { const v = window.visualViewport; return !!v && (v.scale || 1) > 1.05; };
  let fitKey = "";
  // The size of the set follows one rule on every screen (a computer at any zoom, a tablet, a phone, a TV): the set is ONE picture, drawn at 430 px wide (how it looks
  // on a computer at 100%), and the whole picture is scaled to fit. Its parts never change shape or size against each other, so every vent, port and label keeps its
  // proportions. It is as big as 100% unless the window is short or narrow, then it is smaller by the same factor everywhere. The buttons come under it.
  // A computer's zoom is taken out first (pageZoom) and the size is locked: zooming the browser does not make the TV bigger, smaller or different (see SC below).
  const EDGE = 24, BAR_ROOM = 120, BAR_ROOM_COMPACT = 64;
  let cinema = root.getAttribute("data-cinema") === "1";          // Cinema mode: wider and shorter, a 16:9 screen (set before the first paint, see the layout effect above)
  let designW = cinema ? DESIGN_W_CINEMA : DESIGN_W;
  let Hd = 0;                              // the picture's height at its design size (the cone's layout height does not change when it is scaled)
  function fit() {
    if (!alive) return;
    holds.zoom = pinched();
    reserved = miniH();
    const compact = compactNow();
    root.setAttribute("data-compact", compact ? "1" : "0");
    if (!Hd) { layout(); Hd = cone.offsetHeight; }
    const z = pageZoom(), vis = visibleH() * z, vw = (document.documentElement.clientWidth || window.innerWidth) * z;
    const top = root.getBoundingClientRect().top + (window.pageYOffset || 0);
    // While the page is pinched in, a pinch fires this over and over. If nothing about the room the TV has has changed, leave the picture alone: redoing it
    // every time rebuilt the whole 3-D body and reallocated its canvases on every step of the pinch.
    const key = [compact, cinema, z, Math.round(vis / 8), Math.round(vw / 8), Math.round(top / 4), reserved].join("|");
    if (holds.zoom && key === fitKey) return;
    fitKey = key;
    const room = vis - top - (compact ? BAR_ROOM_COMPACT : BAR_ROOM) - EDGE - reserved;
    // SC100 is the size at 100% zoom. A computer's zoom is cancelled out: zoom z makes a CSS pixel z times bigger, so the picture is made 1/z as many CSS pixels
    // and stays exactly the same size on the screen. Zooming in cannot make the TV bigger or smaller.
    SC = Math.max(0.3, Math.min(1, room / Hd, (vw * 0.94) / designW)) / z;
    cone.style.setProperty("--atv-k", SC.toFixed(4));
    // Cinema mode on a small screen: the words on the screen would shrink with the picture, so they are made bigger by the same amount (up to 1.5 times) to stay readable
    cone.style.setProperty("--atv-tz", cinema ? Math.min(1.5, Math.max(1, 0.78 / (SC * z))).toFixed(3) : "1");
    cone.style.marginBottom = `${((SC - 1) * Hd).toFixed(1)}px`;     // the picture is smaller than its layout box: take the difference back
    root.style.minHeight = `${Math.max(visibleH() - top - 12 - reserved, 300)}px`;
    layout();
  }

  // ---------- pointer, finger and keyboard ----------
  const mouseish = (e) => e.pointerType === "mouse" || e.pointerType === "pen";
  on(slot, "pointerenter", (e) => { if (mouseish(e)) holds.hover = true; });
  on(slot, "pointerleave", (e) => { if (mouseish(e)) holds.hover = false; });
  on(slot, "pointerdown", (e) => { if (e.pointerType === "touch") holds.touch = true; });
  const letGo = () => later(() => { holds.touch = false; }, 1500);
  on(slot, "pointerup", letGo); on(slot, "pointercancel", letGo);
  on(pod, "focusin", (e) => { if (e.target.matches && e.target.matches(":focus-visible")) holds.focus = true; });
  on(pod, "focusout", () => { holds.focus = false; });

  // tap the screen: stand it straight, lift the pod out and grow it to the window; its edge stays in view
  const grown = () => ({ left: -8, top: -6, width: window.innerWidth + 16, height: window.innerHeight + 12 });
  // The pod is drawn inside the scaled picture; out in the window it is not scaled any more, so it keeps the picture's scale (s) while it flies out and grows to 1.
  const setBox = (b, s = 1) => { pod.style.left = `${b.left}px`; pod.style.top = `${b.top}px`; pod.style.width = `${b.width / s}px`; pod.style.height = `${b.height / s}px`; pod.style.transform = `scale(${s})`; };
  function whenDone(fn) {
    if (reduce) { fn(); return; }
    let called = false;
    const h = (e) => { if (e.target === pod && e.propertyName === "width") f(); };
    const f = () => { if (called) return; called = true; pod.removeEventListener("transitionend", h); fn(); };
    pod.addEventListener("transitionend", h); later(f, 800);
  }
  function whenStraight(fn) {
    const t0 = performance.now();
    (function w() { if (offFront() < 0.5 || performance.now() - t0 > 1300) fn(); else raf(w); })();
  }
  function openIt() {
    if (isOpen || busy) return;
    busy = true; holds.open = true;
    whenStraight(doOpen);
  }
  // full size: the words grow until they fill most of the screen. A bigger window gets bigger words; a long text in a short window stays at its normal size and scrolls.
  const ZK_MAX = 1.9, ZK_FILL = 0.84;
  function fitZoom(animate, tries = 0) {
    if (!alive || !isOpen) return;
    // wait until the screen has finished growing (a slow device can still be mid-way when the open animation is reported done)
    const pr = pod.getBoundingClientRect(), g = grown();
    if ((Math.abs(pr.width - g.width) > 2 || Math.abs(pr.height - g.height) > 2) && tries < 30) { later(() => fitZoom(animate, tries + 1), 100); return; }
    const kids = [...content.children]; if (!kids.length) return;
    kids.forEach((n) => { n.style.transition = "none"; });
    content.style.setProperty("--atv-zk", "1");
    const cs = getComputedStyle(content);
    const availH = content.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    const availW = content.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const a = kids[0].getBoundingClientRect(), z = kids[kids.length - 1].getBoundingClientRect();
    const natH = z.bottom - a.top, natW = Math.max(...kids.map((n) => n.getBoundingClientRect().width));
    let k = natH > 0 && natW > 0 ? Math.min(ZK_MAX, (availH * ZK_FILL) / natH, availW / natW) : 1;
    if (!(k > 1.02)) k = 1;
    if (animate && !reduce) { void content.offsetWidth; kids.forEach((n) => { n.style.transition = ""; }); }
    content.style.setProperty("--atv-zk", k.toFixed(3));
    if (!animate || reduce) { void content.offsetWidth; kids.forEach((n) => { n.style.transition = ""; }); }
  }
  let zoomTimer = 0;
  function doOpen() {
    const r = pod.getBoundingClientRect();
    // the shell stays in the stand as a still picture while the real pod flies out of it
    ghost = pod.cloneNode(true);
    ghost.classList.add("atv-ghost"); ghost.setAttribute("aria-hidden", "true"); ghost.setAttribute("inert", "");
    ghost.querySelectorAll("[data-testid]").forEach((n) => n.removeAttribute("data-testid"));
    ghost.querySelectorAll("video").forEach((n) => n.remove());      // the player's picture stays in the real screen; a copy would start loading the song again
    front.insertBefore(ghost, pod);
    portal.appendChild(pod);
    pod.classList.add("atv-fixed"); setBox(r, SC); void pod.offsetWidth;
    pod.classList.add("atv-moving", "atv-zoomed");
    document.documentElement.classList.add("atv-lock");
    pod.setAttribute("role", "dialog"); pod.setAttribute("aria-modal", "true"); pod.setAttribute("aria-label", "About Dan The Man TV");
    content.setAttribute("tabindex", "0");
    openBtn.hidden = true; readBtn.setAttribute("aria-expanded", "true");
    closeBtn.hidden = false; setBox(grown()); isOpen = true;
    whenDone(() => { busy = false; pod.style.transform = "none"; closeBtn.focus(); fitZoom(true); });
  }
  function settle() {
    kidsReset();
    pod.classList.remove("atv-fixed", "atv-moving", "atv-zoomed"); pod.removeAttribute("style");
    if (ghost) { front.insertBefore(pod, ghost); ghost.remove(); ghost = null; } else if (pod.parentNode !== front) front.appendChild(pod);
    document.documentElement.classList.remove("atv-lock");
    pod.removeAttribute("role"); pod.removeAttribute("aria-modal"); pod.removeAttribute("aria-label"); content.removeAttribute("tabindex");
    openBtn.hidden = false; readBtn.setAttribute("aria-expanded", "false"); closeBtn.hidden = true;
  }
  function kidsReset() { content.style.removeProperty("--atv-zk"); [...content.children].forEach((n) => { n.style.transition = ""; }); }
  function closeIt() {
    if (!isOpen || busy) return;
    busy = true;
    pod.classList.remove("atv-zoomed"); setBox(front.getBoundingClientRect(), SC); isOpen = false; closeBtn.hidden = true;
    whenDone(() => {
      settle();
      busy = false; holds.hover = false; readBtn.focus({ preventScroll: true });
      later(() => { holds.open = false; }, 900);
    });
  }
  // the screen itself: the page decides what a tap does (the About page plays the member's Favorites). The Read button grows the text to the window.
  on(openBtn, "click", (e) => { e.preventDefault(); const tap = getTap && getTap(); if (tap && !isOpen) tap(); });
  on(readBtn, "click", (e) => { e.preventDefault(); if (!isOpen) openIt(); });
  on(closeBtn, "click", closeIt);
  on(pod, "click", (e) => { if (isOpen && !screen.contains(e.target)) closeIt(); });

  // Bluetooth button: it blinks like it is pairing and opens a small reminder that the person pairs their speaker or headphones in their own
  // Bluetooth settings. (A web page cannot pair a speaker itself.) Got it, a tap outside, Esc or the button again closes it.
  function btOpen() { btpop.hidden = false; btn.setAttribute("aria-expanded", "true"); btn.classList.add("atv-pairing"); btOk.focus({ preventScroll: true }); }
  function btClose(refocus) {
    if (btpop.hidden) return;
    btpop.hidden = true; btn.setAttribute("aria-expanded", "false"); btn.classList.remove("atv-pairing");
    if (refocus) btn.focus({ preventScroll: true });
  }
  on(btn, "click", (e) => { e.stopPropagation(); if (btpop.hidden) btOpen(); else btClose(true); });
  on(btOk, "click", () => btClose(true));
  on(document, "pointerdown", (e) => { if (!btpop.hidden && !btpop.contains(e.target) && !btn.contains(e.target)) btClose(false); });
  on(document, "keydown", (e) => {
    if (e.key === "Escape") { if (!btpop.hidden) btClose(true); else closeIt(); return; }
    if (e.key === "Tab" && isOpen && !busy) {
      // the opened TV is a window of its own: Tab stays inside it
      const list = [...pod.querySelectorAll("a[href], button, [tabindex='0']")].filter((n) => !n.hidden && n.getClientRects().length);
      if (!list.length) return;
      const first = list[0], lastEl = list[list.length - 1];
      if (!pod.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); first.focus(); }
    }
  });
  on(window, "resize", () => { if (isOpen && !busy) { setBox(grown()); clearTimeout(zoomTimer); zoomTimer = later(() => fitZoom(false), 160); } });
  on(window, "resize", fit);
  on(window, "orientationchange", fit);
  if (window.visualViewport) on(window.visualViewport, "resize", fit);
  on(window, "load", fit);
  if (mq && mq.addEventListener) {
    const change = () => { reduce = mq.matches; applyScheme(cur); syncSpinBtn(); };
    mq.addEventListener("change", change); offs.push(() => mq.removeEventListener("change", change));
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
  // the player bar comes and goes (a song starts, the big player opens): size the set again when its height changes
  const miniWatch = setInterval(() => { if (!isOpen && !busy && miniH() !== reserved) fit(); }, 400);
  offs.push(() => clearInterval(miniWatch));

  // ---------- the force-field switch under the TV (the choice is kept on this device) ----------
  const LOOK_KEY = "dtm_ff";
  const lookSel = q("[data-ff-sel]");
  function setLook(name, save) {
    if (name !== "diamond" && name !== "octagon" && name !== "grid") return;
    FF.style = name;
    if (FF.H) ffBuild(FF.H);
    if (lookSel) lookSel.value = name;
    if (save) { try { localStorage.setItem(LOOK_KEY, name); } catch { /* private window: it just is not remembered */ } }
  }
  if (lookSel) on(lookSel, "change", () => setLook(lookSel.value, true));
  let savedLook = null;
  try { savedLook = localStorage.getItem(LOOK_KEY); } catch { /* ignore */ }
  setLook(savedLook, false);

  // ---------- finish: Steel, Wood, Mint, Cherry, Space age, Gold, Quartz (the choice is kept on this device) ----------
  const FIN_KEY = "dtm_tv_finish";
  const finSel = q("[data-fin-sel]");
  const finNames = FINISHES.map(([id]) => id);
  // the Wood finish's veneer is a picture drawn once, the first time Wood is picked (the 3-D body draws the same one for the sides)
  let plyDone = false;
  function ensureVeneer() {
    if (plyDone) return;
    plyDone = true;
    try { root.style.setProperty("--atv-ply", `url(${plywoodDataUrl(640, 704, 7)})`); } catch { /* the plain wood color stays */ }
  }
  // the Quartz finish's slab photo for the page's own parts (the 3-D body fetches the same photo itself); fetched the first time Quartz is picked
  let slabDone = false;
  function ensureSlab() {
    if (slabDone) return;
    slabDone = true;
    import("./quartzTv.js").then((m) => m.slabPlateUrl()).then((u) => { if (alive && u) root.style.setProperty("--atv-slab", `url(${u})`); }).catch(() => { slabDone = false; });
  }
  function setFinish(name, save) {
    if (!finNames.includes(name)) return;
    if (name === "wood") ensureVeneer();
    if (name === "quartz") ensureSlab();
    root.setAttribute("data-finish", name);
    if (glBody) glBody.setFinish(name);
    if (finSel) finSel.value = name;
    if (save) { try { localStorage.setItem(FIN_KEY, name); } catch { /* private window: it just is not remembered */ } }
  }
  if (finSel) on(finSel, "change", () => setFinish(finSel.value, true));
  let savedFin = null;
  try { savedFin = localStorage.getItem(FIN_KEY); } catch { /* ignore */ }
  setFinish(finNames.includes(savedFin) ? savedFin : "steel", false);

  // ---------- turn it on again: the screen switches on once more ----------
  const replayBtn = q("[data-replay]");
  if (replayBtn) on(replayBtn, "click", () => { screen.classList.add("atv-again"); void screen.offsetWidth; screen.classList.remove("atv-again"); });

  // ---------- spin while it plays ----------
  const spinBtn = q("[data-spin]");
  function syncSpinBtn() {
    if (!spinBtn) return;
    spinBtn.disabled = reduce;
    spinBtn.title = reduce ? "Your device is set to reduce motion, so the TV stays still" : "Keep the TV turning while a song plays on its screen";
    spinBtn.setAttribute("aria-pressed", String(keepSpin && !reduce));
  }
  if (spinBtn) on(spinBtn, "click", () => { if (reduce) return; keepSpin = !keepSpin; syncSpinBtn(); });
  syncSpinBtn();

  // ---------- Cinema mode: a wide TV with a 16:9 screen ----------
  // The whole picture is drawn wider (DESIGN_W_CINEMA) and its face is as tall as the 16:9 screen needs (about-tv.css, data-cinema), then it is fitted to the window like
  // the usual shape. The size it was measured at (Hd) is measured again, because the picture's height is different now.
  const cineBtn = q("[data-classic-btn]");              // the "Classic mode" switch: pressed = the older shape
  const syncCineBtn = () => { if (cineBtn) cineBtn.setAttribute("aria-pressed", String(!cinema)); };
  function setCinema(on, save) {
    on = !!on;
    if (save) { try { localStorage.setItem(CLASSIC_KEY, on ? "0" : "1"); } catch { /* private window: it just is not remembered */ } }
    if (on === cinema) { syncCineBtn(); return; }
    cinema = on; designW = on ? DESIGN_W_CINEMA : DESIGN_W;
    root.setAttribute("data-cinema", on ? "1" : "0");
    syncCineBtn();
    Hd = 0; fitKey = "";
    fit();
    if (isOpen && !busy) fitZoom(false);
  }
  if (cineBtn) on(cineBtn, "click", () => setCinema(!cinema, true));
  syncCineBtn();

  // ---------- lights row: follow the song (the default), or one of the looks from the sample ----------
  const LIT_KEY = "dtm_tv_lights";
  const LIT = {   // hues are color-wheel angles; the effects pulse quickly, like a song in Beat mode
    frost: { style: "pulse", hues: [195, 215], per: 0.6, glow: 70, fx: "frost" },
    flames: { style: "pulse", hues: [18, 42], per: 0.6, glow: 70, fx: "flames" },
    fireice: { style: "pulse", hues: [200, 20], per: 0.6, glow: 70, fx: "fireice" },
    haunted: { style: "pulse", hues: [275, 28], per: 0.6, glow: 65, fx: "haunted" },
    maypole: { style: "maypole-cw", hues: [187, 300, 48], per: 2.8, glow: 85, fx: null },
  };
  const litSel = q("[data-lit-sel]");
  let litPick = "song";
  const litRaw = () => {
    const p = LIT[litPick];
    if (!p || holds.video) return getLights();       // a song playing on the screen sets its own lights; the pick is for when nothing plays
    const rgb = p.hues.map((h) => hsl2rgb(h, 100, 58));
    return { style: p.style, glow: p.glow, fx: p.fx, per: p.per, spin: 7, rgb: [rgb[0], rgb[1], rgb[2] || rgb[1]] };
  };
  // while a song plays on the screen the menu shows "Follow the song" and is switched off: nobody changes a song's lights from here
  function syncLit() {
    if (!litSel) return;
    litSel.value = holds.video ? "song" : litPick;
    litSel.disabled = !!holds.video;
    litSel.title = holds.video ? "The song sets the lights while it plays on the screen" : "";
  }
  function setLit(name, save) {
    if (name !== "song" && !LIT[name]) return;
    litPick = name;
    syncLit();
    applyScheme(view(litRaw()));
    if (save) { try { localStorage.setItem(LIT_KEY, name); } catch { /* private window: it just is not remembered */ } }
  }
  if (litSel) on(litSel, "change", () => setLit(litSel.value, true));
  try { const sl = localStorage.getItem(LIT_KEY); if (sl && LIT[sl]) litPick = sl; } catch { /* ignore */ }

  // ---------- the solid body ----------
  // Loaded after the page is up. If the browser cannot draw it (or the graphics chip takes it away later) the stack of slices is used instead, as before.
  function disableGl() {
    if (!glOn) return;
    glOn = false; root.classList.remove("atv-gl");
    if (glBody) { glBody.dispose(); glBody = null; }
    shown = -1; layout();
  }
  async function enableGl() {
    try {
      const mod = await import("./TvBodyGl.js");
      if (!alive) return;
      const body = await mod.makeBody({ slot, before: turn2, onLost: () => disableGl() });
      if (!body) return;
      if (!alive) { body.dispose(); return; }
      glBody = body; glOn = true; root.classList.add("atv-gl");
      body.setFinish(root.getAttribute("data-finish") || "steel");
      offs.push(() => { if (glBody) { glBody.dispose(); glBody = null; } });
      layout();
    } catch { /* the slices stay */ }
  }

  // ---------- go ----------
  fit();
  later(() => root.setAttribute("data-fitted", "1"), 250);       // from here on the words may grow and shrink smoothly (see about-tv.css)
  setLit(litPick, false);
  offs.push(subscribeLights(() => applyScheme(view(litRaw()))));
  offs.push(subscribeBeat(onBeat));
  raf(atomFrame);
  raf((t) => { last = t; frame(t); });
  enableGl();

  // for tests: freeze the turn and the atom, or change the lights, from the page's console
  root.__atv = {
    spin: { set(a) { forced = true; angle = a; apply(); }, free() { forced = false; }, get angle() { return angle; } },
    atom: { set(t) { atomFix = t; }, free() { atomFix = null; } },
    scheme: (raw) => applyScheme(view(raw)),
    hold(name, on2) {
      const was = holds[name]; holds[name] = !!on2;
      if (name === "video" && was !== !!on2) { syncLit(); applyScheme(view(litRaw())); }
    },
    look: (name) => setLook(name, true),
    finish: (name) => setFinish(name, true), lights: (name) => setLit(name, true),
    cinema: (on) => setCinema(on, true), get cinemaOn() { return cinema; },
    fit, wins, FF, get open() { return isOpen; }, get frames() { return atomN; },
  };

  return () => {
    alive = false;
    rafs.forEach((id) => cancelAnimationFrame(id)); rafs.clear();
    timers.forEach((id) => clearTimeout(id)); timers.clear();
    offs.forEach((off) => off());
    if (isOpen || busy || ghost) settle();
    document.documentElement.classList.remove("atv-lock");
    document.body.classList.remove("atv-clip");
    delete root.__atv;
  };
}
